import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
    LayoutDashboard, CreditCard, ScanLine, Users, LogOut,
    Plus, Gift, Stamp, Star, TrendingUp, Award, Loader2,
    ArrowRight, ChevronRight, QrCode, Search, Palette, Upload, X,
    Coffee, UtensilsCrossed, Wheat, Scissors, ShoppingBag, Dumbbell,
    HeartPulse, Flower2, Crown, Leaf, Clock, Zap, Sparkles, Heart, Gem,
    Music, Wine, Camera, BookOpen, Plane, PawPrint, Bike, Cake, Pizza,
    Car, Anchor, Flame, Stethoscope, GraduationCap, Trophy, Wrench,
    IceCreamCone, Shirt, Sun, Mountain, Fish, Gamepad2, Headphones,
    MapPin, Umbrella, TreePine, Soup, Sandwich, Beer, Store,
    Percent, Euro, Layers, DollarSign, BadgeCheck, Phone, ShieldCheck, ShieldOff, Download, Handshake, Tag, CalendarDays, ImageIcon, Trash2, Pencil, Send, Bell, Activity, Filter, Lock, Share2, Copy, Check, PowerOff, AlertTriangle
} from 'lucide-react';
import { CardPattern } from '@/components/cards/CardPattern';
import QrCameraScanner from '@/components/QrCameraScanner';
import CustomerDetailDialog from '@/components/CustomerDetailDialog';
import { QRCodeSVG } from 'qrcode.react';
import { Footer } from '@/components/Footer';

const iconMap = {
    coffee: Coffee, utensils: UtensilsCrossed, wheat: Wheat, scissors: Scissors,
    shopping: ShoppingBag, dumbbell: Dumbbell, health: HeartPulse, flower: Flower2,
    crown: Crown, leaf: Leaf, clock: Clock, zap: Zap, sparkles: Sparkles, gift: Gift,
    music: Music, wine: Wine, camera: Camera, book: BookOpen, plane: Plane,
    paw: PawPrint, palette: Palette, bike: Bike, cake: Cake, pizza: Pizza,
    car: Car, anchor: Anchor, flame: Flame, stethoscope: Stethoscope,
    graduation: GraduationCap, trophy: Trophy, wrench: Wrench, icecream: IceCreamCone,
    shirt: Shirt, sun: Sun, mountain: Mountain, fish: Fish, gamepad: Gamepad2,
    headphones: Headphones, mappin: MapPin, umbrella: Umbrella, tree: TreePine,
    soup: Soup, sandwich: Sandwich, beer: Beer, store: Store,
    star: Star, heart: Heart, gem: Gem, stamp: Stamp,
};

const stampIconMap = iconMap;

function ThemeIcon({ icon, className }) {
    const Icon = iconMap[icon] || Gift;
    return <Icon className={className} />;
}

function StampIcon({ style, className, colorStyle }) {
    const Icon = stampIconMap[style] || Stamp;
    return <Icon className={className} style={colorStyle} />;
}

const cardThemes = [
    { cat: 'Activité', name: 'Café', icon: 'coffee', from: '#78350f', to: '#451a03', pattern: 'circles', stamp: 'coffee' },
    { cat: 'Activité', name: 'Restaurant', icon: 'utensils', from: '#991b1b', to: '#7f1d1d', pattern: 'waves', stamp: 'utensils' },
    { cat: 'Activité', name: 'Boulangerie', icon: 'wheat', from: '#b45309', to: '#92400e', pattern: 'dots', stamp: 'wheat' },
    { cat: 'Activité', name: 'Coiffure', icon: 'scissors', from: '#7e22ce', to: '#6b21a8', pattern: 'diagonal', stamp: 'scissors' },
    { cat: 'Activité', name: 'Boutique', icon: 'shopping', from: '#be185d', to: '#9d174d', pattern: 'circles', stamp: 'shopping' },
    { cat: 'Activité', name: 'Sport', icon: 'dumbbell', from: '#0369a1', to: '#075985', pattern: 'diagonal', stamp: 'dumbbell' },
    { cat: 'Activité', name: 'Bien-être', icon: 'health', from: '#0d9488', to: '#0f766e', pattern: 'waves', stamp: 'health' },
    { cat: 'Activité', name: 'Fleuriste', icon: 'flower', from: '#db2777', to: '#be185d', pattern: 'dots', stamp: 'flower' },
    { cat: 'Activité', name: 'Pizzeria', icon: 'pizza', from: '#dc2626', to: '#b91c1c', pattern: 'circles', stamp: 'pizza' },
    { cat: 'Activité', name: 'Bar', icon: 'beer', from: '#d97706', to: '#92400e', pattern: 'bubbles', stamp: 'beer' },
    { cat: 'Activité', name: 'Glacier', icon: 'icecream', from: '#ec4899', to: '#db2777', pattern: 'confetti', stamp: 'icecream' },
    { cat: 'Activité', name: 'Animalerie', icon: 'paw', from: '#65a30d', to: '#4d7c0f', pattern: 'dots', stamp: 'paw' },
    { cat: 'Activité', name: 'Garage', icon: 'car', from: '#475569', to: '#334155', pattern: 'diagonal', stamp: 'car' },
    { cat: 'Activité', name: 'Librairie', icon: 'book', from: '#7c3aed', to: '#6d28d9', pattern: 'grid', stamp: 'book' },
    { cat: 'Activité', name: 'Musique', icon: 'music', from: '#6366f1', to: '#4f46e5', pattern: 'waves', stamp: 'music' },
    { cat: 'Activité', name: 'Santé', icon: 'stethoscope', from: '#0891b2', to: '#0e7490', pattern: 'crosses', stamp: 'stethoscope' },
    { cat: 'Ambiance', name: 'Luxe', icon: 'crown', from: '#292524', to: '#1c1917', pattern: 'diagonal', stamp: 'crown' },
    { cat: 'Ambiance', name: 'Nature', icon: 'leaf', from: '#15803d', to: '#166534', pattern: 'leaves', stamp: 'leaf' },
    { cat: 'Ambiance', name: 'Retro', icon: 'clock', from: '#c2410c', to: '#9a3412', pattern: 'dots', stamp: 'clock' },
    { cat: 'Ambiance', name: 'Moderne', icon: 'zap', from: '#334155', to: '#1e293b', pattern: 'none', stamp: 'zap' },
    { cat: 'Ambiance', name: 'Festif', icon: 'sparkles', from: '#c026d3', to: '#a21caf', pattern: 'confetti', stamp: 'sparkles' },
    { cat: 'Ambiance', name: 'Minimaliste', icon: 'gift', from: '#525252', to: '#404040', pattern: 'none', stamp: 'stamp' },
    { cat: 'Ambiance', name: 'Tropical', icon: 'sun', from: '#ea580c', to: '#c2410c', pattern: 'waves', stamp: 'sun' },
    { cat: 'Ambiance', name: 'Montagne', icon: 'mountain', from: '#1e3a5f', to: '#0f172a', pattern: 'triangles', stamp: 'mountain' },
    { cat: 'Ambiance', name: 'Maritime', icon: 'anchor', from: '#0369a1', to: '#0c4a6e', pattern: 'waves', stamp: 'anchor' },
    { cat: 'Ambiance', name: 'Aventure', icon: 'mappin', from: '#b45309', to: '#78350f', pattern: 'zigzag', stamp: 'mappin' },
];

const stampStyles = [
    { name: 'Classique', value: 'stamp' },
    { name: 'Etoile', value: 'star' },
    { name: 'Coeur', value: 'heart' },
    { name: 'Diamant', value: 'gem' },
    { name: 'Cafe', value: 'coffee' },
    { name: 'Feuille', value: 'leaf' },
    { name: 'Couronne', value: 'crown' },
    { name: 'Fleur', value: 'flower' },
    { name: 'Eclair', value: 'zap' },
    { name: 'Flamme', value: 'flame' },
    { name: 'Trophee', value: 'trophy' },
    { name: 'Patte', value: 'paw' },
    { name: 'Musique', value: 'music' },
    { name: 'Pizza', value: 'pizza' },
    { name: 'Gateau', value: 'cake' },
    { name: 'Soleil', value: 'sun' },
];

const iconToStampMap = Object.fromEntries(Object.keys(iconMap).map(k => [k, k]));

const colorPresets = [
    { name: 'Emeraude', from: '#059669', to: '#047857' },
    { name: 'Ocean', from: '#2563eb', to: '#1d4ed8' },
    { name: 'Violet', from: '#7c3aed', to: '#6d28d9' },
    { name: 'Feu', from: '#ea580c', to: '#c2410c' },
    { name: 'Rose', from: '#e11d48', to: '#be123c' },
    { name: 'Or', from: '#d97706', to: '#b45309' },
    { name: 'Nuit', from: '#374151', to: '#1f2937' },
    { name: 'Cyan', from: '#0891b2', to: '#0e7490' },
];

const patternOptions = [
    { name: 'Aucun', value: 'none' },
    { name: 'Cercles', value: 'circles' },
    { name: 'Points', value: 'dots' },
    { name: 'Vagues', value: 'waves' },
    { name: 'Diagonales', value: 'diagonal' },
    { name: 'Damier', value: 'checker' },
    { name: 'Etoiles', value: 'stars' },
    { name: 'Hexagones', value: 'hexagons' },
    { name: 'Confettis', value: 'confetti' },
    { name: 'Zigzag', value: 'zigzag' },
    { name: 'Losanges', value: 'diamonds' },
    { name: 'Rayures', value: 'stripes' },
    { name: 'Grille', value: 'grid' },
    { name: 'Croix', value: 'crosses' },
    { name: 'Triangles', value: 'triangles' },
    { name: 'Bulles', value: 'bubbles' },
    { name: 'Coeurs', value: 'hearts' },
    { name: 'Floral', value: 'floral' },
    { name: 'Ecailles', value: 'scales' },
    { name: 'Marocain', value: 'morocco' },
    { name: 'Pluie', value: 'rain' },
    { name: 'Feuilles', value: 'leaves' },
];

function formatTierBenefits(reward) {
    if (!reward || typeof reward === 'string') return reward || '-';
    const parts = [];
    if (reward.discount > 0) parts.push(`-${reward.discount}%`);
    if (reward.free_product) parts.push(`${reward.free_product}/${reward.free_product_freq === 'visit' ? 'visite' : 'mois'}`);
    if (reward.points_multiplier > 1) parts.push(`x${reward.points_multiplier} pts`);
    if (reward.vip_access) parts.push('VIP');
    return parts.length > 0 ? parts.join(' + ') : '-';
}

function getTextColor(card, field) {
    return card[`text_color_${field}`] || card.card_text_color || '#ffffff';
}

function LockedFeature({ feature, requiredPlan }) {
    const planLabels = { pro: 'Pro', premium: 'Premium' };
    return (
        <Card className="border-slate-200 rounded-2xl" data-testid={`locked-${feature}`}>
            <CardContent className="p-10 text-center space-y-4">
                <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mx-auto">
                    <Lock className="w-7 h-7 text-slate-400" />
                </div>
                <div>
                    <h3 className="text-lg font-bold text-slate-800">Fonctionnalité verrouillée</h3>
                    <p className="text-sm text-slate-500 mt-1">
                        Cette fonctionnalité est disponible à partir du forfait <strong className="text-emerald-600">{planLabels[requiredPlan] || requiredPlan}</strong>.
                    </p>
                </div>
                <div className="bg-emerald-50 rounded-xl p-4 text-sm text-slate-700">
                    Contactez l'administrateur FidelRun pour upgrader votre forfait :
                    <br /><a href="tel:0693463628" className="text-emerald-600 font-medium hover:underline">06 93 46 36 28</a>
                </div>
            </CardContent>
        </Card>
    );
}

export default function MerchantDashboard() {
    const { user, logout } = useAuth();
    const [stats, setStats] = useState(null);
    const [cards, setCards] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [selectedCustomerId, setSelectedCustomerId] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [planFeatures, setPlanFeatures] = useState(null);
    const [upgradeMsg, setUpgradeMsg] = useState(null);
    // Wizard state
    const [wizardOpen, setWizardOpen] = useState(false);
    const [wizardStep, setWizardStep] = useState(1);
    const [editingCardId, setEditingCardId] = useState(null);
    const defaultTierRewards = [
        { discount: 5, free_product: '', free_product_freq: 'month', vip_access: false, points_multiplier: 1 },
        { discount: 10, free_product: '1 cafe', free_product_freq: 'month', vip_access: false, points_multiplier: 1.5 },
        { discount: 15, free_product: '1 patisserie', free_product_freq: 'month', vip_access: false, points_multiplier: 2 },
        { discount: 20, free_product: '1 menu', free_product_freq: 'month', vip_access: true, points_multiplier: 3 },
    ];
    const [newCard, setNewCard] = useState({
        name: '', type: 'stamp', stamp_goal: 10, points_per_euro: 1,
        reward_description: '', reward_points_cost: 100, expiration_days: 365,
        bonus_birthday: false, bonus_social: false,
        card_color_from: '#059669', card_color_to: '#047857',
        card_pattern: 'circles', card_logo: '', card_background: '',
        card_text_color: '#ffffff', card_text_bold: true,
        text_color_label: '', text_color_title: '', text_color_subtitle: '',
        text_color_progress: '', text_color_reward: '', text_color_footer: '',
        card_icon: '', card_stamp_style: 'stamp',
        cashback_percent: 5, cashback_min_redeem: 5,
        amount_goal: 200,
        tier_thresholds: [0, 200, 500, 1000],
        tier_rewards: defaultTierRewards,
        paliers_config: [{ points: 100, reward: '5€ de remise' }, { points: 500, reward: '25€ de remise' }],
        reward_expiry_days: 30,
        birthday_reward_type: '', birthday_reward_value: 0,
        birthday_reward_description: '', birthday_reward_expiry_days: 7,
        referral_enabled: false, referral_sponsor_bonus: 5, referral_godchild_bonus: 3,
        social_instagram: '', social_facebook: '', social_tiktok: '', social_snapchat: '',
        social_bonus: 2, review_google: '', review_tripadvisor: '', review_bonus: 3,
        shop_address: '', shop_phone: '',
    });
    // Scanner state
    const [qrInput, setQrInput] = useState('');
    const [scanResult, setScanResult] = useState(null);
    const [addAmount, setAddAmount] = useState(1);
    const [scanning, setScanning] = useState(false);
    const [stampLoading, setStampLoading] = useState(false);
    const [lastStampedIndex, setLastStampedIndex] = useState(-1);
    const [showCamera, setShowCamera] = useState(false);
    const [partnerships, setPartnerships] = useState([]);
    const [partnerSearch, setPartnerSearch] = useState('');
    const [partnerResults, setPartnerResults] = useState([]);
    const [partnerSharePct, setPartnerSharePct] = useState(20);
    const [partnerWelcomeBonus, setPartnerWelcomeBonus] = useState(5);
    const [promos, setPromos] = useState([]);
    const [promoDialogOpen, setPromoDialogOpen] = useState(false);
    const [editingPromo, setEditingPromo] = useState(null);
    const [promoForm, setPromoForm] = useState({ title: '', description: '', photo: '', original_price: '', promo_price: '', start_date: '', end_date: '', conditions: '', limited_quantity: '', promo_code: '' });
    const [pushHistory, setPushHistory] = useState([]);
    const [pushDialogOpen, setPushDialogOpen] = useState(false);
    const [pushForm, setPushForm] = useState({ title: '', message: '' });
    const [pushSending, setPushSending] = useState(false);
    const [activityLogs, setActivityLogs] = useState([]);
    const [activitySummary, setActivitySummary] = useState(null);
    const [activityFilter, setActivityFilter] = useState('');
    const [activityLoading, setActivityLoading] = useState(false);
    const [shareCard, setShareCard] = useState(null);
    const [linkCopied, setLinkCopied] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            const [statsRes, cardsRes, custRes, txRes, partRes, promosRes, pushRes, planRes] = await Promise.all([
                api.get('/stats/merchant'),
                api.get('/cards'),
                api.get('/customers'),
                api.get('/transactions'),
                api.get('/partners'),
                api.get('/promos').catch(() => ({ data: [] })),
                api.get('/push/history').catch(() => ({ data: [] })),
                api.get('/plan/features'),
            ]);
            setStats(statsRes.data);
            setCards(cardsRes.data);
            setCustomers(custRes.data);
            setTransactions(txRes.data);
            setPartnerships(partRes.data);
            setPromos(promosRes.data);
            setPushHistory(pushRes.data);
            setPlanFeatures(planRes.data);
        } catch (err) {
            console.error(err);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const fetchActivityLogs = useCallback(async (filter = '') => {
        setActivityLoading(true);
        try {
            const params = filter ? `?action=${filter}&limit=50` : '?limit=50';
            const [logsRes, summaryRes] = await Promise.all([
                api.get(`/activity-logs${params}`),
                api.get('/activity-logs/summary'),
            ]);
            setActivityLogs(logsRes.data.logs);
            setActivitySummary(summaryRes.data);
        } catch (err) {
            console.error(err);
        }
        setActivityLoading(false);
    }, []);

    const resetWizard = () => {
        setWizardOpen(false);
        setWizardStep(1);
        setEditingCardId(null);
        setNewCard({ name: '', type: 'stamp', stamp_goal: 10, points_per_euro: 1, reward_description: '', reward_points_cost: 100, expiration_days: 365, bonus_birthday: false, bonus_social: false, card_color_from: '#059669', card_color_to: '#047857', card_pattern: 'circles', card_logo: '', card_background: '', card_text_color: '#ffffff', card_text_bold: true, text_color_label: '', text_color_title: '', text_color_subtitle: '', text_color_progress: '', text_color_reward: '', text_color_footer: '', card_icon: '', card_stamp_style: 'stamp', cashback_percent: 5, cashback_min_redeem: 5, amount_goal: 200, tier_thresholds: [0, 200, 500, 1000], tier_rewards: defaultTierRewards, paliers_config: [{ points: 100, reward: '5€ de remise' }, { points: 500, reward: '25€ de remise' }], reward_expiry_days: 30, birthday_reward_type: '', birthday_reward_value: 0, birthday_reward_description: '', birthday_reward_expiry_days: 7, referral_enabled: false, referral_sponsor_bonus: 5, referral_godchild_bonus: 3, social_instagram: '', social_facebook: '', social_tiktok: '', social_snapchat: '', social_bonus: 2, review_google: '', review_tripadvisor: '', review_bonus: 3 });
    };

    const handleSaveCard = async () => {
        try {
            if (editingCardId) {
                await api.put(`/cards/${editingCardId}`, newCard);
                toast.success('Carte modifiée avec succès !');
            } else {
                await api.post('/cards', newCard);
                toast.success('Carte créée avec succès !');
            }
            resetWizard();
            fetchData();
        } catch (err) {
            toast.error(editingCardId ? 'Erreur lors de la modification' : 'Erreur lors de la creation');
        }
    };

    const handleEditCard = (card) => {
        setEditingCardId(card.id);
        setNewCard({
            name: card.name || '',
            type: card.type || 'stamp',
            stamp_goal: card.stamp_goal || 10,
            points_per_euro: card.points_per_euro || 1,
            reward_description: card.reward_description || '',
            reward_points_cost: card.reward_points_cost || 100,
            expiration_days: card.expiration_days || 365,
            bonus_birthday: card.bonus_birthday || false,
            bonus_social: card.bonus_social || false,
            card_color_from: card.card_color_from || '#059669',
            card_color_to: card.card_color_to || '#047857',
            card_pattern: card.card_pattern || 'circles',
            card_logo: card.card_logo || '',
            card_background: card.card_background || '',
            card_text_color: card.card_text_color || '#ffffff',
            card_text_bold: card.card_text_bold !== false,
            text_color_label: card.text_color_label || '',
            text_color_title: card.text_color_title || '',
            text_color_subtitle: card.text_color_subtitle || '',
            text_color_progress: card.text_color_progress || '',
            text_color_reward: card.text_color_reward || '',
            text_color_footer: card.text_color_footer || '',
            card_icon: card.card_icon || '',
            card_stamp_style: card.card_stamp_style || 'stamp',
            cashback_percent: card.cashback_percent || 5,
            cashback_min_redeem: card.cashback_min_redeem || 5,
            amount_goal: card.amount_goal || 200,
            tier_thresholds: card.tier_thresholds || [0, 200, 500, 1000],
            tier_rewards: card.tier_rewards || defaultTierRewards,
            paliers_config: card.paliers_config || [{ points: 100, reward: '5€ de remise' }, { points: 500, reward: '25€ de remise' }],
            reward_expiry_days: card.reward_expiry_days || 30,
            birthday_reward_type: card.birthday_reward_type || '',
            birthday_reward_value: card.birthday_reward_value || 0,
            birthday_reward_description: card.birthday_reward_description || '',
            birthday_reward_expiry_days: card.birthday_reward_expiry_days || 7,
            referral_enabled: card.referral_enabled || false,
            referral_sponsor_bonus: card.referral_sponsor_bonus || 5,
            referral_godchild_bonus: card.referral_godchild_bonus || 3,
            social_instagram: card.social_instagram || '',
            social_facebook: card.social_facebook || '',
            social_tiktok: card.social_tiktok || '',
            social_snapchat: card.social_snapchat || '',
            social_bonus: card.social_bonus || 2,
            review_google: card.review_google || '',
            review_tripadvisor: card.review_tripadvisor || '',
            review_bonus: card.review_bonus || 3,
            shop_address: card.shop_address || '',
            shop_phone: card.shop_phone || '',
        });
        setWizardStep(1);
        setWizardOpen(true);
    };

    const handleScan = async (code) => {
        const qrCode = code || qrInput;
        if (!qrCode) {
            toast.error('Entrez le code QR du client');
            return;
        }
        setScanning(true);
        try {
            const res = await api.post('/scan', { qr_code_id: qrCode.trim().toUpperCase() });
            setScanResult(res.data);
            toast.success(`Client ${res.data.customer.name} trouvé !`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Client non trouvé');
            setScanResult(null);
        } finally {
            setScanning(false);
        }
    };

    const handleCameraScan = (decodedText) => {
        setShowCamera(false);
        setQrInput(decodedText);
        handleScan(decodedText);
    };

    const handleAddTransaction = async (type, amount) => {
        if (!scanResult) return;
        const qty = amount || addAmount;
        setStampLoading(true);
        try {
            const res = await api.post('/transactions', {
                wallet_id: scanResult.wallet.id,
                type: type,
                amount: qty,
                description: type === 'redeem_palier' ? `Palier ${qty} pts utilisé` : 'Ajouté par commerçant',
            });
            setScanResult(prev => ({ ...prev, wallet: res.data.wallet }));
            if (type === 'redeem_palier') {
                const palier = (scanResult.card.paliers_config || []).find(p => p.points === qty);
                toast.success(`Remise appliquée : ${palier?.reward || qty + ' pts déduits'} !`, { duration: 4000 });
            } else if (type === 'add_purchase') {
                const ptsAdded = Math.round(qty * (scanResult.card.points_per_euro || 1));
                toast.success(`Achat de ${qty}€ enregistré (+${ptsAdded} points) !`);
            } else {
                toast.success(type.includes('stamp') ? 'Tampon ajouté !' : 'Points ajoutés !');
            }
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally {
            setStampLoading(false);
        }
    };

    const handleClickStamp = async (index) => {
        if (!scanResult || stampLoading) return;
        if (index < scanResult.wallet.stamps) return; // already stamped
        setStampLoading(true);
        setLastStampedIndex(index);
        try {
            const res = await api.post('/transactions', {
                wallet_id: scanResult.wallet.id,
                type: 'add_stamp',
                amount: 1,
                description: 'Tampon ajouté par commerçant',
            });
            setScanResult(prev => ({ ...prev, wallet: res.data.wallet }));
            const newStamps = res.data.wallet.stamps;
            if (newStamps >= scanResult.card.stamp_goal) {
                toast.success('Carte complète ! Le client peut réclamer sa récompense !', { duration: 4000 });
            } else {
                toast.success(`Tampon ${newStamps}/${scanResult.card.stamp_goal} ajouté !`);
            }
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally {
            setStampLoading(false);
            setTimeout(() => setLastStampedIndex(-1), 600);
        }
    };

    const handleRedeemForCustomer = async () => {
        if (!scanResult) return;
        setStampLoading(true);
        try {
            const cardType = scanResult.card.type;
            const txType = cardType === 'cashback' ? 'use_cashback' : 'redeem';
            const res = await api.post('/transactions', {
                wallet_id: scanResult.wallet.id,
                type: txType,
                amount: 1,
                description: cardType === 'cashback' ? 'Cashback utilisé' : 'Récompense validée par commerçant',
            });
            setScanResult(prev => ({ ...prev, wallet: res.data.wallet }));
            const msg = cardType === 'cashback'
                ? `Cashback de ${(scanResult.wallet.cashback_balance || 0).toFixed(2)}€ utilise pour ${scanResult.customer.name} !`
                : `Récompense validée ! "${scanResult.card.reward_description}" offert à ${scanResult.customer.name}. Compteur remis à zéro.`;
            toast.success(msg, { duration: 5000 });
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de la validation');
        } finally {
            setStampLoading(false);
        }
    };

    const handleBirthdayReward = async () => {
        if (!scanResult) return;
        try {
            const res = await api.post('/transactions', {
                wallet_id: scanResult.wallet.id,
                type: 'birthday_reward',
                amount: scanResult.card.birthday_reward_value || 0,
                description: `Récompense anniversaire: ${scanResult.card.birthday_reward_type}`,
            });
            setScanResult(prev => ({ ...prev, wallet: res.data.wallet, birthday_eligible: false }));
            toast.success(`Cadeau anniversaire offert a ${scanResult.customer.name} !`, { duration: 5000 });
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    const searchPartners = async (q) => {
        setPartnerSearch(q);
        if (q.length < 2) { setPartnerResults([]); return; }
        try {
            const res = await api.get(`/partners/search?q=${encodeURIComponent(q)}`);
            setPartnerResults(res.data);
        } catch { setPartnerResults([]); }
    };

    const sendPartnerRequest = async (email) => {
        try {
            await api.post('/partners/request', { receiver_email: email, share_percent: partnerSharePct, welcome_bonus: partnerWelcomeBonus });
            toast.success('Demande envoyee !');
            setPartnerSearch(''); setPartnerResults([]);
            fetchData();
        } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    };

    const acceptPartner = async (id) => {
        try { await api.put(`/partners/${id}/accept`); toast.success('Partenariat accepte !'); fetchData(); }
        catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    };

    const rejectPartner = async (id) => {
        try { await api.put(`/partners/${id}/reject`); toast.success('Demande refusee'); fetchData(); }
        catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    };

    const deletePartner = async (id) => {
        try { await api.delete(`/partners/${id}`); toast.success('Partenariat supprimé'); fetchData(); }
        catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    };

    const handleDeleteCard = async (cardId) => {
        try {
            await api.delete(`/cards/${cardId}`);
            toast.success('Carte supprimée');
            fetchData();
        } catch (err) {
            toast.error('Erreur de suppression');
        }
    };

    const updateNewCard = (key, val) => setNewCard(prev => ({ ...prev, [key]: val }));

    const handleLogoUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 500000) {
            toast.error('Logo trop volumineux (max 500 Ko)');
            return;
        }
        const reader = new FileReader();
        reader.onload = () => updateNewCard('card_logo', reader.result);
        reader.readAsDataURL(file);
    };

    const handleBackgroundUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 2000000) {
            toast.error('Image trop volumineuse (max 2 Mo)');
            return;
        }
        const reader = new FileReader();
        reader.onload = () => updateNewCard('card_background', reader.result);
        reader.readAsDataURL(file);
    };

    const applyTheme = (theme) => {
        setNewCard(prev => ({
            ...prev,
            card_color_from: theme.from,
            card_color_to: theme.to,
            card_pattern: theme.pattern,
            card_icon: theme.icon,
            card_stamp_style: theme.stamp,
        }));
        toast.success(`Theme "${theme.name}" applique !`);
    };

    const openPromoDialog = (promo = null) => {
        if (promo) {
            setEditingPromo(promo.id);
            setPromoForm({ title: promo.title, description: promo.description || '', photo: promo.photo || '', original_price: promo.original_price || '', promo_price: promo.promo_price || '', start_date: promo.start_date || '', end_date: promo.end_date || '', conditions: promo.conditions || '', limited_quantity: promo.limited_quantity || '', promo_code: promo.promo_code || '' });
        } else {
            setEditingPromo(null);
            setPromoForm({ title: '', description: '', photo: '', original_price: '', promo_price: '', start_date: '', end_date: '', conditions: '', limited_quantity: '', promo_code: '' });
        }
        setPromoDialogOpen(true);
    };

    const handlePromoPhotoUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 3000000) { toast.error('Image trop volumineuse (max 3 Mo)'); return; }
        const reader = new FileReader();
        reader.onload = () => setPromoForm(prev => ({ ...prev, photo: reader.result }));
        reader.readAsDataURL(file);
    };

    const savePromo = async () => {
        if (!promoForm.title || !promoForm.start_date || !promoForm.end_date) { toast.error('Remplissez titre, date debut et date fin'); return; }
        try {
            const payload = { ...promoForm, original_price: parseFloat(promoForm.original_price) || 0, promo_price: parseFloat(promoForm.promo_price) || 0, limited_quantity: parseInt(promoForm.limited_quantity) || 0 };
            if (editingPromo) {
                await api.put(`/promos/${editingPromo}`, payload);
                toast.success('Promo mise a jour !');
            } else {
                await api.post('/promos', payload);
                toast.success('Promo créée ! Tous les clients ont été notifiés.');
            }
            setPromoDialogOpen(false);
            fetchData();
        } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    };

    const deletePromo = async (id) => {
        try { await api.delete(`/promos/${id}`); toast.success('Promo supprimée'); fetchData(); }
        catch (err) { toast.error('Erreur'); }
    };

    const getPromoStatus = (p) => {
        const now = new Date().toISOString().slice(0, 10);
        if (now < p.start_date) return { label: 'Programmee', color: 'bg-blue-100 text-blue-700' };
        if (now > p.end_date) return { label: 'Expiree', color: 'bg-slate-100 text-slate-500' };
        return { label: 'Active', color: 'bg-emerald-100 text-emerald-700' };
    };

    const sendPushNotification = async () => {
        if (!pushForm.title || !pushForm.message) { toast.error('Remplissez le titre et le message'); return; }
        setPushSending(true);
        try {
            const res = await api.post('/push/send', pushForm);
            toast.success(`Notification envoyee ! Push: ${res.data.push_recipients}, In-app: ${res.data.in_app_recipients} clients`);
            setPushDialogOpen(false);
            setPushForm({ title: '', message: '' });
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi');
        } finally { setPushSending(false); }
    };

    return (
        <div className="min-h-screen bg-gray-50/80">
            {/* Merchant Blocked - No Plan (Activation Pending) */}
            {user?.role === 'merchant' && !user?.plan && !user?.plan_expired && (
                <div className="fixed inset-0 z-[100] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="merchant-pending-overlay">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center space-y-5">
                        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
                            <Clock className="w-8 h-8 text-amber-500" />
                        </div>
                        <h2 className="text-xl font-bold text-slate-900">Activation en attente</h2>
                        <p className="text-sm text-slate-600 leading-relaxed">
                            Votre compte a bien été créé ! Pour accéder à votre tableau de bord et commencer à utiliser FidelRun, votre forfait doit être activé par l'administrateur.
                        </p>
                        <div className="bg-slate-50 rounded-xl p-4 text-left space-y-2">
                            <p className="text-xs font-medium text-slate-500">Pour activer votre forfait :</p>
                            <p className="text-sm text-slate-700">Appelez-nous au <a href="tel:0693463628" className="text-emerald-600 font-semibold hover:underline">06 93 46 36 28</a></p>
                        </div>
                        <Button variant="outline" size="sm" onClick={logout} className="text-slate-500" data-testid="pending-logout-btn">
                            <LogOut className="w-4 h-4 mr-2" /> Se déconnecter
                        </Button>
                    </div>
                </div>
            )}
            {/* Merchant Blocked - Expired Plan */}
            {user?.plan_expired && (
                <div className="fixed inset-0 z-[100] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="merchant-blocked-overlay">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center space-y-5">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                            <ShieldOff className="w-8 h-8 text-red-500" />
                        </div>
                        <h2 className="text-xl font-bold text-slate-900">Forfait expiré</h2>
                        <p className="text-sm text-slate-600 leading-relaxed">
                            Votre abonnement FidelRun a expiré. L'accès à votre tableau de bord est temporairement suspendu.
                        </p>
                        <div className="bg-slate-50 rounded-xl p-4 text-left space-y-1">
                            <p className="text-xs font-medium text-slate-500">Pour réactiver votre compte :</p>
                            <p className="text-sm text-slate-700">Appelez-nous au <a href="tel:0693463628" className="text-emerald-600 font-semibold hover:underline">06 93 46 36 28</a></p>
                        </div>
                        <Button variant="outline" size="sm" onClick={logout} className="text-slate-500" data-testid="blocked-logout-btn">
                            <LogOut className="w-4 h-4 mr-2" /> Se déconnecter
                        </Button>
                    </div>
                </div>
            )}
            {/* Header */}
            <header className="glass-header sticky top-0 z-50" data-testid="merchant-header">
                <div className="max-w-screen-xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="w-12 h-12 rounded-lg overflow-hidden">
                            <img src="/logo-fr-small.png" alt="FidelRun" className="w-12 h-12 object-contain" />
                        </div>
                        <span className="font-heading text-lg font-bold text-emerald-950">FidelRun</span>
                        <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs ml-2 hidden sm:inline-flex">Commerçant</Badge>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-slate-700 hidden sm:block">{user?.business_name || user?.name}</span>
                        <Button variant="ghost" size="sm" onClick={logout} className="text-slate-500 hover:text-red-500" data-testid="merchant-logout-btn">
                            <LogOut className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            </header>

            <main className="max-w-screen-xl mx-auto px-4 sm:px-6 py-6">
                {/* Plan Banner */}
                {planFeatures && (
                    <div className="mb-4 flex items-center gap-2 text-xs">
                        <span className={`px-2.5 py-1 rounded-full font-semibold ${
                            planFeatures.plan === 'premium' ? 'bg-amber-100 text-amber-800' :
                            planFeatures.plan === 'pro' ? 'bg-purple-100 text-purple-700' :
                            'bg-blue-100 text-blue-700'
                        }`} data-testid="plan-badge">
                            Forfait {planFeatures.plan?.charAt(0).toUpperCase() + planFeatures.plan?.slice(1)}
                        </span>
                        <span className="text-slate-400">
                            {planFeatures.cards_limit >= 999 ? 'Cartes illimitées' : `${cards.length}/${planFeatures.cards_limit} carte(s)`}
                        </span>
                    </div>
                )}

                <Tabs defaultValue="overview" className="space-y-6">
                    <div className="tabs-wrapper">
                    <TabsList className="bg-white border border-emerald-100/50 rounded-xl p-1 w-full justify-start mobile-tabs scrollbar-hide" data-testid="merchant-tabs" style={{ flexWrap: 'nowrap' }}>
                        <TabsTrigger value="overview" className="data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700 rounded-lg gap-1.5 text-sm shrink-0" data-testid="tab-overview">
                            <LayoutDashboard className="w-4 h-4" /> Aperçu
                        </TabsTrigger>
                        <TabsTrigger value="cards" className="data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700 rounded-lg gap-1.5 text-sm shrink-0" data-testid="tab-merchant-cards">
                            <CreditCard className="w-4 h-4" /> Cartes
                        </TabsTrigger>
                        <TabsTrigger value="scanner" className="data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700 rounded-lg gap-1.5 text-sm shrink-0" data-testid="tab-scanner">
                            <ScanLine className="w-4 h-4" /> Scanner
                        </TabsTrigger>
                        <TabsTrigger value="customers" className="data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700 rounded-lg gap-1.5 text-sm shrink-0" data-testid="tab-customers">
                            <Users className="w-4 h-4" /> Clients
                        </TabsTrigger>
                        <TabsTrigger value="partners" className="data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700 rounded-lg gap-1.5 text-sm shrink-0" data-testid="tab-partners">
                            <Handshake className="w-4 h-4" /> Partenaires
                            {partnerships.filter(p => p.status === 'pending' && p.receiver_id === user?.id).length > 0 && (
                                <span className="w-2 h-2 rounded-full bg-orange-400" />
                            )}
                        </TabsTrigger>
                        <TabsTrigger value="promos" className={`data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700 rounded-lg gap-1.5 text-sm shrink-0 ${planFeatures && !planFeatures.promos ? 'opacity-60' : ''}`} data-testid="tab-promos">
                            <Tag className="w-4 h-4" /> Promotions
                            {planFeatures && !planFeatures.promos ? <Lock className="w-3 h-3 text-slate-400" /> :
                                promos.filter(p => { const now = new Date().toISOString().slice(0,10); return now >= p.start_date && now <= p.end_date; }).length > 0 && (
                                    <Badge className="bg-emerald-500 text-white border-0 text-[9px] px-1.5 py-0 ml-1">{promos.filter(p => { const now = new Date().toISOString().slice(0,10); return now >= p.start_date && now <= p.end_date; }).length}</Badge>
                                )
                            }
                        </TabsTrigger>
                        <TabsTrigger value="notifications" className={`data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700 rounded-lg gap-1.5 text-sm shrink-0 ${planFeatures && !planFeatures.push ? 'opacity-60' : ''}`} data-testid="tab-notifications">
                            <Bell className="w-4 h-4" /> Notifications
                            {planFeatures && !planFeatures.push && <Lock className="w-3 h-3 text-slate-400" />}
                        </TabsTrigger>
                        <TabsTrigger value="activity" className={`data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700 rounded-lg gap-1.5 text-sm shrink-0 ${planFeatures && !planFeatures.activity_logs ? 'opacity-60' : ''}`} data-testid="tab-activity" onClick={() => planFeatures?.activity_logs && fetchActivityLogs(activityFilter)}>
                            <Activity className="w-4 h-4" /> Activité
                            {planFeatures && !planFeatures.activity_logs && <Lock className="w-3 h-3 text-slate-400" />}
                        </TabsTrigger>
                    </TabsList>
                    </div>

                    {/* OVERVIEW */}
                    <TabsContent value="overview" className="space-y-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="font-heading text-2xl font-bold">Bonjour, {user?.name}</h1>
                                <p className="text-sm text-slate-500">Voici un aperçu de votre activité</p>
                            </div>
                        </div>

                        {stats && (
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="merchant-stats">
                                {[
                                    { label: 'Cartes actives', value: stats.total_cards, icon: CreditCard, color: 'emerald' },
                                    { label: 'Membres', value: stats.total_members, icon: Users, color: 'emerald' },
                                    { label: 'Total distribue', value: stats.total_issued, icon: TrendingUp, color: 'emerald' },
                                    { label: 'Récompenses', value: stats.total_redeems, icon: Award, color: 'emerald' },
                                ].map((s, i) => (
                                    <Card key={i} className="border-emerald-100/50 rounded-2xl hover:border-emerald-200 transition-all" data-testid={`stat-card-${i}`}>
                                        <CardContent className="p-5">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                                                    <s.icon className="w-5 h-5 text-emerald-600" />
                                                </div>
                                            </div>
                                            <p className="font-heading text-3xl font-bold">{s.value}</p>
                                            <p className="text-sm text-slate-500">{s.label}</p>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )}

                        {/* Recent Transactions */}
                        <Card className="border-emerald-100/50 rounded-2xl" data-testid="recent-transactions">
                            <CardHeader>
                                <CardTitle className="font-heading text-lg">Transactions recentes</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {transactions.length === 0 ? (
                                    <p className="text-sm text-slate-400 text-center py-4">Aucune transaction</p>
                                ) : (
                                    <div className="space-y-2">
                                        {transactions.slice(0, 5).map(tx => (
                                            <div key={tx.id} className="flex items-center justify-between p-3 bg-gray-50/80 rounded-lg">
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${tx.type === 'redeem' ? 'bg-amber-100' : 'bg-emerald-100'}`}>
                                                        {tx.type === 'redeem' ? <Gift className="w-4 h-4 text-amber-600" /> : <Plus className="w-4 h-4 text-emerald-600" />}
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium">{tx.type === 'add_stamp' ? 'Tampon' : tx.type === 'add_points' ? 'Points' : tx.type === 'redeem' ? 'Récompense' : tx.type}</p>
                                                        <p className="text-xs text-slate-400">{new Date(tx.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</p>
                                                    </div>
                                                </div>
                                                <span className="font-heading font-bold text-sm text-emerald-600">+{tx.amount}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* CARDS */}
                    <TabsContent value="cards" className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h2 className="font-heading text-xl font-bold">Mes Cartes de Fidelite</h2>
                            {planFeatures && cards.length >= planFeatures.cards_limit ? (
                                <Button disabled className="bg-slate-300 text-slate-500 gap-1.5 cursor-not-allowed" data-testid="create-card-btn-locked">
                                    <Lock className="w-4 h-4" /> Limite atteinte ({planFeatures.cards_limit})
                                </Button>
                            ) : (
                                <Button onClick={() => { resetWizard(); setWizardOpen(true); }} className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1.5 active:scale-95 transition-all" data-testid="create-card-btn">
                                    <Plus className="w-4 h-4" /> Créer une carte
                                </Button>
                            )}
                        </div>

                        {cards.length === 0 ? (
                            <Card className="border-emerald-100/50 rounded-2xl">
                                <CardContent className="p-8 text-center">
                                    <CreditCard className="w-12 h-12 text-emerald-200 mx-auto mb-4" />
                                    <p className="text-slate-500 mb-4">Aucune carte créée</p>
                                    <Button onClick={() => { resetWizard(); setWizardOpen(true); }} className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1.5" data-testid="create-first-card-btn">
                                        <Plus className="w-4 h-4" /> Créer ma première carte
                                    </Button>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {cards.map(card => (
                                    <Card key={card.id} className={`border-emerald-100/50 rounded-2xl hover:border-emerald-200 transition-all overflow-hidden ${card.admin_disabled ? 'ring-2 ring-red-200' : ''}`} data-testid={`merchant-card-${card.id}`}>
                                        {/* Card color preview strip */}
                                        <div
                                            className="h-3 w-full"
                                            style={{ background: `linear-gradient(90deg, ${card.card_color_from || '#059669'}, ${card.card_color_to || '#047857'})` }}
                                        />
                                        {card.admin_disabled && (
                                            <div className="bg-red-50 border-b border-red-100 px-4 py-2 flex items-start gap-2" data-testid={`admin-disabled-badge-${card.id}`}>
                                                <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                                                <div className="text-xs leading-tight">
                                                    <p className="font-semibold text-red-700">Programme désactivé par l'administrateur</p>
                                                    {card.admin_disabled_reason && (
                                                        <p className="text-red-600/90 mt-0.5">Motif : {card.admin_disabled_reason}</p>
                                                    )}
                                                    <p className="text-red-500/70 mt-0.5">Le scan et l'attribution de points sont bloqués. Contactez le support pour réactiver.</p>
                                                </div>
                                            </div>
                                        )}
                                        <CardContent className="p-5">
                                            <div className="flex items-start justify-between mb-3">
                                                <div className="flex items-center gap-3">
                                                    {card.card_logo ? (
                                                        <img src={card.card_logo} className="w-10 h-10 rounded-lg object-cover border border-slate-100" alt="" />
                                                    ) : (
                                                        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${card.card_color_from || '#059669'}, ${card.card_color_to || '#047857'})` }}>
                                                            <ThemeIcon icon={card.card_icon || 'gift'} className="w-5 h-5 text-white" />
                                                        </div>
                                                    )}
                                                    <div>
                                                        <h3 className="font-heading font-bold">{card.name}</h3>
                                                        <Badge className={`border-0 text-xs mt-0.5 ${
                                                            card.type === 'stamp' ? 'bg-amber-100 text-amber-700' :
                                                            card.type === 'points' ? 'bg-blue-100 text-blue-700' :
                                                            card.type === 'cashback' ? 'bg-green-100 text-green-700' :
                                                            card.type === 'tier' ? 'bg-purple-100 text-purple-700' :
                                                            'bg-cyan-100 text-cyan-700'
                                                        }`}>
                                                            {card.type === 'stamp' ? 'Tampons' : 
                                                             card.type === 'points' ? 'Points' : 
                                                             card.type === 'cashback' ? 'Cashback' : 
                                                             card.type === 'tier' ? 'Paliers' : 'Montant'}
                                                        </Badge>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <Button variant="ghost" size="sm" onClick={() => { setShareCard(card); setLinkCopied(false); }} className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 text-xs" data-testid={`share-card-${card.id}`}>
                                                        <Share2 className="w-3.5 h-3.5 mr-1" /> Partager
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => handleEditCard(card)} className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 text-xs" data-testid={`edit-card-${card.id}`}>
                                                        Modifier
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => handleDeleteCard(card.id)} className="text-slate-400 hover:text-red-500 text-xs" data-testid={`delete-card-${card.id}`}>
                                                        Supprimer
                                                    </Button>
                                                </div>
                                            </div>
                                            <div className="space-y-2 text-sm text-slate-600">
                                                <div className="flex justify-between">
                                                    <span>Récompense</span>
                                                    <span className="font-medium">{card.reward_description}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span>Objectif</span>
                                                    <span className="font-medium">{card.type === 'stamp' ? `${card.stamp_goal} tampons` : `${card.reward_points_cost} points`}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span>Membres</span>
                                                    <span className="font-heading font-bold text-emerald-600">{card.members_count || 0}</span>
                                                </div>
                                            </div>
                                            {(card.bonus_birthday || card.bonus_social) && (
                                                <div className="flex gap-2 mt-3">
                                                    {card.bonus_birthday && <Badge className="bg-emerald-50 text-emerald-600 border-0 text-xs">Anniversaire</Badge>}
                                                    {card.bonus_social && <Badge className="bg-emerald-50 text-emerald-600 border-0 text-xs">Social</Badge>}
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )}

                        {/* Create Card Wizard */}
                        <Dialog open={wizardOpen} onOpenChange={(open) => { if (!open) resetWizard(); }}>
                            <DialogContent className={`rounded-2xl ${wizardStep >= 2 ? 'sm:max-w-2xl' : 'sm:max-w-md'} max-h-[85vh] flex flex-col`} data-testid="create-card-dialog">
                                <DialogHeader className="shrink-0">
                                    <DialogTitle className="font-heading">
                                        {editingCardId ? 'Modifier la carte' : 'Créer une carte'} {wizardStep === 1 ? '- Type' : wizardStep === 2 ? '- Configuration' : wizardStep === 3 ? '- Options' : '- Design'}
                                    </DialogTitle>
                                    <div className="flex gap-1 mt-2">
                                        {[1, 2, 3, 4].map(s => (
                                            <div key={s} className={`h-1 flex-1 rounded-full ${s <= wizardStep ? 'bg-emerald-500' : 'bg-slate-200'}`} />
                                        ))}
                                    </div>
                                </DialogHeader>
                                <div className="overflow-y-auto flex-1 min-h-0">

                                {wizardStep === 1 && (
                                    <div className="space-y-4 py-4">
                                        <p className="text-sm text-slate-500">Quel type de programme souhaitez-vous ?</p>
                                        <div className="grid grid-cols-2 gap-3">
                                            {[
                                                { key: 'stamp', icon: Stamp, label: 'Tampons', desc: 'Ex: 10 cafes = 1 offert' },
                                                { key: 'points', icon: Star, label: 'Points', desc: 'Ex: 1€ = 1 point' },
                                                { key: 'cashback', icon: Percent, label: 'Cashback', desc: 'Ex: 5% rembourse en credit' },
                                                { key: 'tier', icon: Layers, label: 'Paliers', desc: '1€ = 1 pt, paliers de remise personnalisables' },
                                                { key: 'amount', icon: Euro, label: 'Montant cumule', desc: 'Ex: 200€ = 20€ offerts' },
                                            ].map(t => (
                                                <button
                                                    key={t.key}
                                                    onClick={() => updateNewCard('type', t.key)}
                                                    className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${newCard.type === t.key ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-emerald-200'}`}
                                                    data-testid={`wizard-${t.key}-btn`}
                                                >
                                                    <t.icon className={`w-7 h-7 ${newCard.type === t.key ? 'text-emerald-600' : 'text-slate-400'}`} />
                                                    <span className="text-sm font-medium">{t.label}</span>
                                                    <span className="text-[10px] text-slate-400 text-center leading-tight">{t.desc}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {wizardStep === 2 && (
                                    <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto pr-1">
                                        <div className="space-y-2">
                                            <Label className="text-sm font-medium">Nom de la carte</Label>
                                            <Input
                                                placeholder="Ex: Carte Cafe Delice"
                                                value={newCard.name}
                                                onChange={e => updateNewCard('name', e.target.value)}
                                                className="h-11"
                                                data-testid="wizard-name-input"
                                            />
                                        </div>

                                        {/* Stamp config */}
                                        {newCard.type === 'stamp' && (
                                            <div className="space-y-2">
                                                <Label className="text-sm font-medium">Nombre de tampons requis</Label>
                                                <Input
                                                    type="number" min="2" max="50"
                                                    value={newCard.stamp_goal}
                                                    onChange={e => updateNewCard('stamp_goal', parseInt(e.target.value) || 10)}
                                                    className="h-11"
                                                    data-testid="wizard-stamp-goal-input"
                                                />
                                            </div>
                                        )}

                                        {/* Points config */}
                                        {newCard.type === 'points' && (
                                            <>
                                                <div className="space-y-2">
                                                    <Label className="text-sm font-medium">Points par euro depense</Label>
                                                    <Input type="number" min="0.1" step="0.1" value={newCard.points_per_euro} onChange={e => updateNewCard('points_per_euro', parseFloat(e.target.value) || 1)} className="h-11" data-testid="wizard-points-euro-input" />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label className="text-sm font-medium">Points pour la récompense</Label>
                                                    <Input type="number" min="10" value={newCard.reward_points_cost} onChange={e => updateNewCard('reward_points_cost', parseInt(e.target.value) || 100)} className="h-11" data-testid="wizard-reward-cost-input" />
                                                </div>
                                            </>
                                        )}

                                        {/* Cashback config */}
                                        {newCard.type === 'cashback' && (
                                            <>
                                                <div className="space-y-2">
                                                    <Label className="text-sm font-medium">Pourcentage de cashback (%)</Label>
                                                    <Input type="number" min="1" max="50" step="0.5" value={newCard.cashback_percent} onChange={e => updateNewCard('cashback_percent', parseFloat(e.target.value) || 5)} className="h-11" data-testid="wizard-cashback-percent-input" />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label className="text-sm font-medium">Seuil minimum pour utiliser le cashback (euros)</Label>
                                                    <Input type="number" min="1" step="0.5" value={newCard.cashback_min_redeem} onChange={e => updateNewCard('cashback_min_redeem', parseFloat(e.target.value) || 5)} className="h-11" data-testid="wizard-cashback-min-input" />
                                                </div>
                                            </>
                                        )}

                                        {/* Tier / Paliers config */}
                                        {newCard.type === 'tier' && (
                                            <>
                                                <div className="space-y-2">
                                                    <Label className="text-sm font-medium">Points par euro depense</Label>
                                                    <Input type="number" min="0.1" step="0.1" value={newCard.points_per_euro} onChange={e => updateNewCard('points_per_euro', parseFloat(e.target.value) || 1)} className="h-11" data-testid="wizard-tier-points-input" />
                                                </div>
                                                <div className="space-y-2">
                                                    <div className="flex items-center justify-between">
                                                        <Label className="text-xs font-medium text-slate-500 uppercase">Paliers de remise</Label>
                                                        <button
                                                            type="button"
                                                            onClick={() => updateNewCard('paliers_config', [...newCard.paliers_config, { points: (newCard.paliers_config.length + 1) * 100, reward: '' }])}
                                                            className="text-xs text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1"
                                                            data-testid="add-palier-btn"
                                                        >
                                                            <Plus className="w-3.5 h-3.5" /> Ajouter un palier
                                                        </button>
                                                    </div>
                                                    <div className="space-y-2">
                                                        {newCard.paliers_config.map((palier, i) => (
                                                            <div key={i} className="flex items-center gap-2 bg-slate-50 rounded-xl p-2.5 border border-slate-100" data-testid={`palier-row-${i}`}>
                                                                <div className="flex items-center gap-1.5 shrink-0">
                                                                    <Input
                                                                        type="number"
                                                                        min="1"
                                                                        value={palier.points}
                                                                        onChange={e => {
                                                                            const p = [...newCard.paliers_config];
                                                                            p[i] = { ...p[i], points: parseInt(e.target.value) || 0 };
                                                                            updateNewCard('paliers_config', p);
                                                                        }}
                                                                        className="h-8 w-20 text-center text-sm font-bold"
                                                                        data-testid={`palier-points-${i}`}
                                                                    />
                                                                    <span className="text-xs text-slate-400 font-medium">pts</span>
                                                                </div>
                                                                <span className="text-slate-300">=</span>
                                                                <Input
                                                                    placeholder="Ex: 5€ de remise"
                                                                    value={palier.reward}
                                                                    onChange={e => {
                                                                        const p = [...newCard.paliers_config];
                                                                        p[i] = { ...p[i], reward: e.target.value };
                                                                        updateNewCard('paliers_config', p);
                                                                    }}
                                                                    className="h-8 text-sm flex-1"
                                                                    data-testid={`palier-reward-${i}`}
                                                                />
                                                                {newCard.paliers_config.length > 1 && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => updateNewCard('paliers_config', newCard.paliers_config.filter((_, j) => j !== i))}
                                                                        className="p-1 text-slate-300 hover:text-red-500 transition-colors shrink-0"
                                                                        data-testid={`remove-palier-${i}`}
                                                                    >
                                                                        <Trash2 className="w-3.5 h-3.5" />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <p className="text-[10px] text-slate-400">Ex: 100 pts = 5€ de remise, 500 pts = 25€ de remise. Les points sont deduits manuellement.</p>
                                                </div>
                                            </>
                                        )}

                                        {/* Amount config */}
                                        {newCard.type === 'amount' && (
                                            <div className="space-y-2">
                                                <Label className="text-sm font-medium">Montant a atteindre (euros)</Label>
                                                <Input type="number" min="10" step="10" value={newCard.amount_goal} onChange={e => updateNewCard('amount_goal', parseFloat(e.target.value) || 200)} className="h-11" data-testid="wizard-amount-goal-input" />
                                            </div>
                                        )}

                                        <div className="space-y-2">
                                            <Label className="text-sm font-medium">Description de la récompense</Label>
                                            <Input
                                                placeholder={newCard.type === 'tier' ? 'Ex: Programme fidélité VIP' : 'Ex: Un café offert'}
                                                value={newCard.reward_description}
                                                onChange={e => updateNewCard('reward_description', e.target.value)}
                                                className="h-11"
                                                data-testid="wizard-reward-input"
                                            />
                                        </div>

                                        <div className="border-t border-slate-100 pt-4 mt-2">
                                            <p className="text-xs text-slate-400 mb-3">Informations du commerce (optionnel — visibles par vos clients)</p>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                <div className="space-y-1.5">
                                                    <Label className="text-sm font-medium">Adresse</Label>
                                                    <Input
                                                        placeholder="Ex: 12 Rue du Commerce, 97490 Saint-Denis"
                                                        value={newCard.shop_address}
                                                        onChange={e => updateNewCard('shop_address', e.target.value)}
                                                        className="h-10 text-sm"
                                                        data-testid="wizard-shop-address"
                                                    />
                                                </div>
                                                <div className="space-y-1.5">
                                                    <Label className="text-sm font-medium">Téléphone</Label>
                                                    <Input
                                                        placeholder="Ex: 0693463628"
                                                        value={newCard.shop_phone}
                                                        onChange={e => updateNewCard('shop_phone', e.target.value)}
                                                        className="h-10 text-sm"
                                                        data-testid="wizard-shop-phone"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {wizardStep === 3 && (
                                    <div className="space-y-4 py-4">
                                        <div className="space-y-2">
                                            <Label className="text-sm font-medium">Duree de validite de la carte (jours)</Label>
                                            <Input
                                                type="number" min="30"
                                                value={newCard.expiration_days}
                                                onChange={e => updateNewCard('expiration_days', parseInt(e.target.value) || 365)}
                                                className="h-11"
                                                data-testid="wizard-expiry-input"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-sm font-medium">Delai d'utilisation du gain (jours)</Label>
                                            <p className="text-xs text-slate-400">Nombre de jours pour utiliser la récompense après l'avoir obtenue</p>
                                            <Input
                                                type="number" min="1"
                                                value={newCard.reward_expiry_days}
                                                onChange={e => updateNewCard('reward_expiry_days', parseInt(e.target.value) || 30)}
                                                className="h-11"
                                                data-testid="wizard-reward-expiry-input"
                                            />
                                        </div>
                                        <div className="p-3 bg-gray-50 rounded-lg space-y-3">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-sm font-medium">Récompense anniversaire</p>
                                                    <p className="text-xs text-slate-400">Offrez un cadeau le jour de l'anniversaire du client</p>
                                                </div>
                                                <Switch
                                                    checked={!!newCard.birthday_reward_type}
                                                    onCheckedChange={v => updateNewCard('birthday_reward_type', v ? 'points' : '')}
                                                    data-testid="wizard-birthday-switch"
                                                />
                                            </div>

                                            {newCard.birthday_reward_type && (
                                                <div className="space-y-3 pt-2 border-t border-slate-200">
                                                    <div>
                                                        <Label className="text-xs font-medium mb-1.5 block">Type de récompense</Label>
                                                        <div className="grid grid-cols-2 gap-2">
                                                            {[
                                                                { value: 'points', label: 'Points bonus', icon: '⭐' },
                                                                { value: 'stamp', label: 'Tampon gratuit', icon: '🎯' },
                                                                { value: 'discount', label: '% Reduction', icon: '🏷️' },
                                                                { value: 'gift', label: 'Lot / Cadeau', icon: '🎁' },
                                                            ].map(opt => (
                                                                <button
                                                                    key={opt.value}
                                                                    type="button"
                                                                    onClick={() => updateNewCard('birthday_reward_type', opt.value)}
                                                                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${
                                                                        newCard.birthday_reward_type === opt.value
                                                                            ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                                                                            : 'bg-white border-slate-200 text-slate-600 hover:border-emerald-200'
                                                                    }`}
                                                                    data-testid={`birthday-type-${opt.value}`}
                                                                >
                                                                    <span>{opt.icon}</span> {opt.label}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    {(newCard.birthday_reward_type === 'points' || newCard.birthday_reward_type === 'stamp') && (
                                                        <div>
                                                            <Label className="text-xs font-medium">
                                                                {newCard.birthday_reward_type === 'points' ? 'Nombre de points offerts' : 'Nombre de tampons offerts'}
                                                            </Label>
                                                            <Input
                                                                type="number" min="1"
                                                                value={newCard.birthday_reward_value}
                                                                onChange={e => updateNewCard('birthday_reward_value', parseInt(e.target.value) || 0)}
                                                                className="h-10 mt-1"
                                                                data-testid="birthday-value-input"
                                                            />
                                                            <p className="text-[11px] text-slate-400 mt-1">
                                                                S'ajoutera automatiquement au cumul du client
                                                            </p>
                                                        </div>
                                                    )}

                                                    {newCard.birthday_reward_type === 'discount' && (
                                                        <div className="space-y-2">
                                                            <div>
                                                                <Label className="text-xs font-medium">Pourcentage de reduction</Label>
                                                                <Input
                                                                    type="number" min="1" max="100"
                                                                    value={newCard.birthday_reward_value}
                                                                    onChange={e => updateNewCard('birthday_reward_value', parseInt(e.target.value) || 0)}
                                                                    className="h-10 mt-1"
                                                                    placeholder="Ex: 20"
                                                                    data-testid="birthday-value-input"
                                                                />
                                                            </div>
                                                            <div>
                                                                <Label className="text-xs font-medium">Description (optionnelle)</Label>
                                                                <Input
                                                                    value={newCard.birthday_reward_description}
                                                                    onChange={e => updateNewCard('birthday_reward_description', e.target.value)}
                                                                    className="h-10 mt-1"
                                                                    placeholder="Ex: Sur tout le magasin"
                                                                    data-testid="birthday-desc-input"
                                                                />
                                                            </div>
                                                            <div>
                                                                <Label className="text-xs font-medium">Valable pendant (jours)</Label>
                                                                <Input
                                                                    type="number" min="1"
                                                                    value={newCard.birthday_reward_expiry_days}
                                                                    onChange={e => updateNewCard('birthday_reward_expiry_days', parseInt(e.target.value) || 7)}
                                                                    className="h-10 mt-1"
                                                                    data-testid="birthday-expiry-input"
                                                                />
                                                            </div>
                                                        </div>
                                                    )}

                                                    {newCard.birthday_reward_type === 'gift' && (
                                                        <div className="space-y-2">
                                                            <div>
                                                                <Label className="text-xs font-medium">Description du lot / cadeau</Label>
                                                                <Input
                                                                    value={newCard.birthday_reward_description}
                                                                    onChange={e => updateNewCard('birthday_reward_description', e.target.value)}
                                                                    className="h-10 mt-1"
                                                                    placeholder="Ex: 1 dessert offert"
                                                                    data-testid="birthday-desc-input"
                                                                />
                                                            </div>
                                                            <div>
                                                                <Label className="text-xs font-medium">Valable pendant (jours)</Label>
                                                                <Input
                                                                    type="number" min="1"
                                                                    value={newCard.birthday_reward_expiry_days}
                                                                    onChange={e => updateNewCard('birthday_reward_expiry_days', parseInt(e.target.value) || 7)}
                                                                    className="h-10 mt-1"
                                                                    data-testid="birthday-expiry-input"
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* MARKETING BOOST - Parrainage */}
                                        <div className="p-3 bg-blue-50/50 rounded-lg space-y-3 border border-blue-100">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-sm font-medium text-blue-800">Parrainage</p>
                                                    <p className="text-xs text-blue-500">Vos clients invitent leurs amis et gagnent des bonus</p>
                                                </div>
                                                <Switch checked={newCard.referral_enabled} onCheckedChange={v => updateNewCard('referral_enabled', v)} data-testid="wizard-referral-switch" />
                                            </div>
                                            {newCard.referral_enabled && (
                                                <div className="grid grid-cols-2 gap-3 pt-2 border-t border-blue-100">
                                                    <div>
                                                        <Label className="text-xs font-medium">Bonus parrain</Label>
                                                        <Input type="number" min="1" value={newCard.referral_sponsor_bonus} onChange={e => updateNewCard('referral_sponsor_bonus', parseInt(e.target.value) || 0)} className="h-9 mt-1" data-testid="referral-sponsor-input" />
                                                        <p className="text-[10px] text-slate-400 mt-0.5">{newCard.type === 'stamp' ? 'tampons' : 'points'}</p>
                                                    </div>
                                                    <div>
                                                        <Label className="text-xs font-medium">Bonus filleul</Label>
                                                        <Input type="number" min="1" value={newCard.referral_godchild_bonus} onChange={e => updateNewCard('referral_godchild_bonus', parseInt(e.target.value) || 0)} className="h-9 mt-1" data-testid="referral-godchild-input" />
                                                        <p className="text-[10px] text-slate-400 mt-0.5">{newCard.type === 'stamp' ? 'tampons' : 'points'}</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* MARKETING BOOST - Reseaux sociaux */}
                                        <div className="p-3 bg-purple-50/50 rounded-lg space-y-3 border border-purple-100">
                                            <div>
                                                <p className="text-sm font-medium text-purple-800">Reseaux sociaux</p>
                                                <p className="text-xs text-purple-500">Bonus pour les clients qui vous suivent (deeplinks vers les apps)</p>
                                            </div>
                                            <div className="space-y-2">
                                                {[
                                                    { key: 'social_instagram', label: 'Instagram', placeholder: 'https://instagram.com/votre_compte', icon: '📸' },
                                                    { key: 'social_facebook', label: 'Facebook', placeholder: 'https://facebook.com/votre_page', icon: '👤' },
                                                    { key: 'social_tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/@votre_compte', icon: '🎵' },
                                                    { key: 'social_snapchat', label: 'Snapchat', placeholder: 'https://snapchat.com/add/votre_compte', icon: '👻' },
                                                ].map(s => (
                                                    <div key={s.key} className="flex items-center gap-2">
                                                        <span className="text-sm w-5 text-center">{s.icon}</span>
                                                        <Input
                                                            placeholder={s.placeholder}
                                                            value={newCard[s.key]}
                                                            onChange={e => updateNewCard(s.key, e.target.value)}
                                                            className="h-9 text-xs flex-1"
                                                            data-testid={`${s.key}-input`}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                            {(newCard.social_instagram || newCard.social_facebook || newCard.social_tiktok || newCard.social_snapchat) && (
                                                <div>
                                                    <Label className="text-xs font-medium">Bonus par réseau suivi</Label>
                                                    <Input type="number" min="1" value={newCard.social_bonus} onChange={e => updateNewCard('social_bonus', parseInt(e.target.value) || 0)} className="h-9 mt-1 w-24" data-testid="social-bonus-input" />
                                                    <p className="text-[10px] text-slate-400 mt-0.5">{newCard.type === 'stamp' ? 'tampons' : 'points'} par réseau</p>
                                                </div>
                                            )}
                                        </div>

                                        {/* MARKETING BOOST - Avis */}
                                        <div className="p-3 bg-amber-50/50 rounded-lg space-y-3 border border-amber-100">
                                            <div>
                                                <p className="text-sm font-medium text-amber-800">Avis en ligne</p>
                                                <p className="text-xs text-amber-500">Bonus pour les clients qui laissent un avis</p>
                                            </div>
                                            <div className="space-y-2">
                                                {[
                                                    { key: 'review_google', label: 'Google', placeholder: 'Lien Google My Business', icon: '⭐' },
                                                    { key: 'review_tripadvisor', label: 'TripAdvisor', placeholder: 'Lien TripAdvisor', icon: '🦉' },
                                                ].map(r => (
                                                    <div key={r.key} className="flex items-center gap-2">
                                                        <span className="text-sm w-5 text-center">{r.icon}</span>
                                                        <Input
                                                            placeholder={r.placeholder}
                                                            value={newCard[r.key]}
                                                            onChange={e => updateNewCard(r.key, e.target.value)}
                                                            className="h-9 text-xs flex-1"
                                                            data-testid={`${r.key}-input`}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                            {(newCard.review_google || newCard.review_tripadvisor) && (
                                                <div>
                                                    <Label className="text-xs font-medium">Bonus par avis</Label>
                                                    <Input type="number" min="1" value={newCard.review_bonus} onChange={e => updateNewCard('review_bonus', parseInt(e.target.value) || 0)} className="h-9 mt-1 w-24" data-testid="review-bonus-input" />
                                                    <p className="text-[10px] text-slate-400 mt-0.5">{newCard.type === 'stamp' ? 'tampons' : 'points'} par avis</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {wizardStep === 4 && (
                                    <div className="py-2 max-h-[65vh] overflow-y-auto pr-1">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                            {/* Left Column: Live Preview (sticky) */}
                                            <div className="md:sticky md:top-0 self-start space-y-3">
                                                <Label className="text-sm font-medium mb-2 block">Aperçu en direct</Label>
                                                <div
                                                    className="relative overflow-hidden rounded-2xl p-5 shadow-xl"
                                                    style={{
                                                        background: newCard.card_background ? 'none' : `linear-gradient(135deg, ${newCard.card_color_from}, ${newCard.card_color_to})`,
                                                        color: newCard.card_text_color,
                                                        fontWeight: newCard.card_text_bold ? 700 : 400,
                                                    }}
                                                    data-testid="card-preview"
                                                >
                                                    {newCard.card_background && (
                                                        <>
                                                            <img src={newCard.card_background} className="absolute inset-0 w-full h-full object-cover" alt="" />
                                                            <div className="absolute inset-0 bg-black/30" />
                                                        </>
                                                    )}
                                                    {!newCard.card_background && <CardPattern pattern={newCard.card_pattern} />}

                                                    <div className="relative z-10 flex items-center gap-3 mb-4">
                                                        {newCard.card_logo ? (
                                                            <img src={newCard.card_logo} className="w-11 h-11 rounded-lg object-cover border border-white/20" alt="" />
                                                        ) : (
                                                            <div className="w-11 h-11 bg-white/20 rounded-lg flex items-center justify-center">
                                                                <ThemeIcon icon={newCard.card_icon || 'gift'} className="w-5 h-5" />
                                                            </div>
                                                        )}
                                                        <div>
                                                            <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: getTextColor(newCard, 'label'), opacity: 0.7 }}>Carte Fidelite</p>
                                                            <p className="font-heading text-base" style={{ color: getTextColor(newCard, 'title'), fontWeight: newCard.card_text_bold ? 700 : 400 }}>{newCard.name || 'Nom de la carte'}</p>
                                                            <p className="text-xs" style={{ color: getTextColor(newCard, 'subtitle'), opacity: 0.8 }}>{user?.business_name || 'Votre commerce'}</p>
                                                        </div>
                                                    </div>
                                                    {newCard.type === 'stamp' ? (
                                                        <div className="relative z-10 flex gap-1.5 mb-3">
                                                            {Array.from({ length: Math.min(newCard.stamp_goal, 10) }).map((_, i) => (
                                                                <div key={i} className={`w-6 h-6 rounded-full flex items-center justify-center ${i < 3 ? 'bg-white' : 'bg-white/20 border border-white/30'}`}>
                                                                    {i < 3 && <StampIcon style={newCard.card_stamp_style} className="w-3.5 h-3.5" colorStyle={{ color: newCard.card_color_from }} />}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : newCard.type === 'points' ? (
                                                        <div className="relative z-10 mb-3 space-y-1.5">
                                                            <div className="bg-white/20 rounded-full h-2.5 overflow-hidden">
                                                                <div className="bg-white rounded-full h-2.5 w-[45%]" />
                                                            </div>
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-xs font-medium" style={{ color: getTextColor(newCard, 'progress') }}>45 / {newCard.reward_points_cost} pts</span>
                                                                <span className="text-[10px]" style={{ color: getTextColor(newCard, 'progress'), opacity: 0.6 }}>{newCard.points_per_euro} pt/euro</span>
                                                            </div>
                                                        </div>
                                                    ) : newCard.type === 'cashback' ? (
                                                        <div className="relative z-10 mb-3 space-y-1.5">
                                                            <div className="flex items-center justify-between mb-1">
                                                                <span className="text-lg font-bold" style={{ color: getTextColor(newCard, 'progress') }}>2,50 €</span>
                                                                <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded-full" style={{ color: getTextColor(newCard, 'progress'), opacity: 0.8 }}>{newCard.cashback_percent}% cashback</span>
                                                            </div>
                                                            <div className="bg-white/20 rounded-full h-2 overflow-hidden">
                                                                <div className="bg-white rounded-full h-2" style={{ width: `${Math.min((2.5 / newCard.cashback_min_redeem) * 100, 100)}%` }} />
                                                            </div>
                                                            <span className="text-[10px]" style={{ color: getTextColor(newCard, 'reward'), opacity: 0.7 }}>Min. {newCard.cashback_min_redeem}€ pour utiliser</span>
                                                        </div>
                                                    ) : newCard.type === 'tier' ? (
                                                        <div className="relative z-10 mb-3 space-y-1.5">
                                                            <div className="flex items-center gap-1">
                                                                {['Bronze', 'Argent', 'Or', 'Platine'].map((n, i) => (
                                                                    <div key={n} className={`flex-1 h-1.5 rounded-full ${i <= 1 ? 'bg-white' : 'bg-white/20'}`} />
                                                                ))}
                                                            </div>
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-xs font-bold" style={{ color: getTextColor(newCard, 'progress') }}>Argent</span>
                                                                <span className="text-[10px]" style={{ color: getTextColor(newCard, 'progress'), opacity: 0.6 }}>250 / {newCard.tier_thresholds[2]} pts → Or</span>
                                                            </div>
                                                            <span className="text-[10px]" style={{ color: getTextColor(newCard, 'reward'), opacity: 0.7 }}>{formatTierBenefits(newCard.tier_rewards[1])}</span>
                                                        </div>
                                                    ) : newCard.type === 'amount' ? (
                                                        <div className="relative z-10 mb-3 space-y-1.5">
                                                            <div className="bg-white/20 rounded-full h-2.5 overflow-hidden">
                                                                <div className="bg-white rounded-full h-2.5 w-[35%]" />
                                                            </div>
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-xs font-medium" style={{ color: getTextColor(newCard, 'progress') }}>70€ / {newCard.amount_goal}€</span>
                                                                <span className="text-[10px]" style={{ color: getTextColor(newCard, 'reward'), opacity: 0.7 }}>{newCard.reward_description || 'Récompense'}</span>
                                                            </div>
                                                        </div>
                                                    ) : null}
                                                    <div className="relative z-10 flex items-center justify-between pt-3 border-t border-white/10">
                                                        <span className="text-[10px] font-mono" style={{ color: getTextColor(newCard, 'footer'), opacity: 0.5 }}>QR-XXXX</span>
                                                        <span className="text-[10px] font-semibold" style={{ color: getTextColor(newCard, 'footer'), opacity: 0.4 }}>FidelRun</span>
                                                    </div>
                                                </div>

                                                {/* Logo Upload */}
                                                <div className="p-3 bg-gray-50/80 rounded-xl">
                                                    <Label className="text-xs font-medium mb-2 block">Logo (optionnel)</Label>
                                                    <div className="flex items-center gap-3">
                                                        {newCard.card_logo ? (
                                                            <div className="relative">
                                                                <img src={newCard.card_logo} className="w-12 h-12 rounded-xl object-cover border-2 border-emerald-200" alt="Logo" />
                                                                <button onClick={() => updateNewCard('card_logo', '')} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center hover:bg-red-600" data-testid="remove-logo-btn">
                                                                    <X className="w-3 h-3" />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <label className="w-12 h-12 rounded-xl border-2 border-dashed border-slate-200 flex items-center justify-center cursor-pointer hover:border-emerald-300 hover:bg-emerald-50 transition-all" data-testid="upload-logo-label">
                                                                <Upload className="w-5 h-5 text-slate-300" />
                                                                <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-testid="upload-logo-input" />
                                                            </label>
                                                        )}
                                                        <p className="text-[10px] text-slate-400">PNG, JPG ou SVG — Max 500 Ko</p>
                                                    </div>
                                                </div>

                                                {/* Background Image Upload */}
                                                <div className="p-3 bg-gray-50/80 rounded-xl">
                                                    <Label className="text-xs font-medium mb-2 block">Image de fond (optionnel)</Label>
                                                    {newCard.card_background ? (
                                                        <div className="relative">
                                                            <img src={newCard.card_background} className="w-full h-20 rounded-lg object-cover border-2 border-emerald-200" alt="Fond" />
                                                            <button onClick={() => updateNewCard('card_background', '')} className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full text-white flex items-center justify-center hover:bg-red-600 shadow-md" data-testid="remove-bg-btn">
                                                                <X className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <label className="flex flex-col items-center gap-1.5 p-4 rounded-lg border-2 border-dashed border-slate-200 cursor-pointer hover:border-emerald-300 hover:bg-emerald-50 transition-all" data-testid="upload-bg-label">
                                                            <Upload className="w-5 h-5 text-slate-300" />
                                                            <span className="text-xs text-slate-400 font-medium">Choisir une image</span>
                                                            <span className="text-[10px] text-slate-300">800 x 500 px recommande — Max 2 Mo</span>
                                                            <input type="file" accept="image/*" onChange={handleBackgroundUpload} className="hidden" data-testid="upload-bg-input" />
                                                        </label>
                                                    )}
                                                    <p className="text-[10px] text-slate-400 mt-1.5">L'image remplace les couleurs et motifs. Le texte reste lisible grace a un overlay sombre.</p>
                                                </div>
                                            </div>

                                            {/* Right Column: Theme & Customization Controls */}
                                            <div className="space-y-4">
                                                {/* Themes by category */}
                                                {['Activité', 'Ambiance'].map(cat => (
                                                    <div key={cat}>
                                                        <Label className="text-xs font-medium mb-1.5 block text-slate-500 uppercase tracking-wide">Themes {cat}</Label>
                                                        <div className="grid grid-cols-4 gap-2" data-testid={`themes-${cat.toLowerCase()}`}>
                                                            {cardThemes.filter(t => t.cat === cat).map(t => {
                                                                const isSelected = newCard.card_color_from === t.from && newCard.card_icon === t.icon;
                                                                return (
                                                                    <button
                                                                        key={t.name}
                                                                        onClick={() => applyTheme(t)}
                                                                        className={`relative overflow-hidden rounded-xl p-2.5 text-white text-left transition-all hover:scale-105 active:scale-95 ${isSelected ? 'ring-2 ring-emerald-400 ring-offset-2' : 'ring-1 ring-white/10'}`}
                                                                        style={{ background: `linear-gradient(135deg, ${t.from}, ${t.to})` }}
                                                                        data-testid={`theme-${t.name.toLowerCase()}`}
                                                                    >
                                                                        {isSelected && <div className="absolute top-1 right-1 w-3 h-3 bg-emerald-400 rounded-full flex items-center justify-center"><Sparkles className="w-2 h-2 text-white" /></div>}
                                                                        <ThemeIcon icon={t.icon} className="w-4 h-4 mb-1 text-white/80" />
                                                                        <p className="text-[10px] font-bold leading-tight">{t.name}</p>
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                ))}

                                                {/* Stamp Style */}
                                                {newCard.type === 'stamp' && (
                                                    <div>
                                                        <Label className="text-xs font-medium mb-1.5 block text-slate-500 uppercase tracking-wide">Style des tampons</Label>
                                                        <div className="grid grid-cols-3 gap-2" data-testid="stamp-styles">
                                                            {stampStyles.map(s => (
                                                                <button
                                                                    key={s.value}
                                                                    onClick={() => updateNewCard('card_stamp_style', s.value)}
                                                                    className={`flex items-center gap-2 p-2 rounded-lg text-xs font-medium transition-all ${newCard.card_stamp_style === s.value ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/20' : 'bg-gray-100 text-slate-600 hover:bg-gray-200'}`}
                                                                    data-testid={`stamp-style-${s.value}`}
                                                                >
                                                                    <StampIcon style={s.value} className="w-4 h-4" />
                                                                    {s.name}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Icon Selector */}
                                                <div>
                                                    <Label className="text-xs font-medium mb-1.5 block text-slate-500 uppercase tracking-wide">Icone de la carte</Label>
                                                    <div className="grid grid-cols-7 gap-1.5" data-testid="icon-selector">
                                                        {Object.entries(iconMap).map(([key, Icon]) => (
                                                            <button
                                                                key={key}
                                                                onClick={() => {
                                                                    updateNewCard('card_icon', key);
                                                                    updateNewCard('card_stamp_style', iconToStampMap[key] || 'circle');
                                                                }}
                                                                className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all ${newCard.card_icon === key ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/20' : 'bg-gray-100 text-slate-500 hover:bg-gray-200'}`}
                                                                title={key}
                                                                data-testid={`icon-${key}`}
                                                            >
                                                                <Icon className="w-4 h-4" />
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>

                                                {/* Color Customization */}
                                                <div>
                                                    <Label className="text-xs font-medium mb-1.5 block text-slate-500 uppercase tracking-wide">Couleurs</Label>
                                                    <div className="grid grid-cols-4 gap-2 mb-2" data-testid="color-presets">
                                                        {colorPresets.map(c => (
                                                            <button
                                                                key={c.name}
                                                                onClick={() => { updateNewCard('card_color_from', c.from); updateNewCard('card_color_to', c.to); }}
                                                                className={`aspect-[2/1] rounded-lg border-2 transition-all hover:scale-105 active:scale-95 ${newCard.card_color_from === c.from ? 'border-emerald-500 ring-1 ring-emerald-500/20' : 'border-transparent'}`}
                                                                style={{ background: `linear-gradient(135deg, ${c.from}, ${c.to})` }}
                                                                title={c.name}
                                                            />
                                                        ))}
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <div className="flex-1 flex items-center gap-1.5">
                                                            <input type="color" value={newCard.card_color_from} onChange={e => updateNewCard('card_color_from', e.target.value)} className="w-7 h-7 rounded cursor-pointer border-0" data-testid="custom-color-from" />
                                                            <Input value={newCard.card_color_from} onChange={e => updateNewCard('card_color_from', e.target.value)} className="h-7 text-xs font-mono" />
                                                        </div>
                                                        <div className="flex-1 flex items-center gap-1.5">
                                                            <input type="color" value={newCard.card_color_to} onChange={e => updateNewCard('card_color_to', e.target.value)} className="w-7 h-7 rounded cursor-pointer border-0" data-testid="custom-color-to" />
                                                            <Input value={newCard.card_color_to} onChange={e => updateNewCard('card_color_to', e.target.value)} className="h-7 text-xs font-mono" />
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Text Style */}
                                                <div>
                                                    <Label className="text-xs font-medium mb-1.5 block text-slate-500 uppercase tracking-wide">Couleurs des textes</Label>
                                                    <div className="space-y-1.5 p-2.5 bg-gray-50 rounded-xl">
                                                        {[
                                                            { key: 'label', name: 'Label "Carte Fidelite"' },
                                                            { key: 'title', name: 'Nom de la carte' },
                                                            { key: 'subtitle', name: 'Nom du commerce' },
                                                            { key: 'progress', name: 'Progression' },
                                                            { key: 'reward', name: 'Récompense' },
                                                            { key: 'footer', name: 'QR / FidelRun' },
                                                        ].map(t => (
                                                            <div key={t.key} className="flex items-center gap-2">
                                                                <input
                                                                    type="color"
                                                                    value={newCard[`text_color_${t.key}`] || newCard.card_text_color || '#ffffff'}
                                                                    onChange={e => updateNewCard(`text_color_${t.key}`, e.target.value)}
                                                                    className="w-6 h-6 rounded cursor-pointer border-0 shrink-0"
                                                                    data-testid={`text-color-${t.key}`}
                                                                />
                                                                <span className="text-[11px] text-slate-600 flex-1">{t.name}</span>
                                                                <button
                                                                    onClick={() => updateNewCard(`text_color_${t.key}`, '')}
                                                                    className="text-[10px] text-slate-400 hover:text-slate-600 px-1"
                                                                    title="Reinitialiser"
                                                                >
                                                                    reset
                                                                </button>
                                                            </div>
                                                        ))}
                                                        <div className="flex items-center gap-2 pt-1.5 border-t border-slate-200">
                                                            <span className="text-[11px] text-slate-500 flex-1">Couleur globale</span>
                                                            <div className="flex gap-1">
                                                                {['#ffffff', '#000000', '#1e293b', '#fef3c7', '#d1fae5'].map(c => (
                                                                    <button key={c} onClick={() => updateNewCard('card_text_color', c)} className={`w-5 h-5 rounded-full border transition-all ${newCard.card_text_color === c ? 'border-emerald-500 scale-110' : 'border-slate-200'}`} style={{ background: c }} />
                                                                ))}
                                                            </div>
                                                            <button
                                                                onClick={() => updateNewCard('card_text_bold', !newCard.card_text_bold)}
                                                                className={`px-2 py-0.5 rounded text-xs font-bold transition-all ${newCard.card_text_bold ? 'bg-emerald-500 text-white' : 'bg-white text-slate-400 border border-slate-200'}`}
                                                                data-testid="text-bold-toggle"
                                                            >
                                                                B
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Pattern */}
                                                <div>
                                                    <Label className="text-xs font-medium mb-1.5 block text-slate-500 uppercase tracking-wide">Motif de fond</Label>
                                                    <div className="grid grid-cols-4 gap-1.5" data-testid="pattern-options">
                                                        {patternOptions.map(p => (
                                                            <button
                                                                key={p.value}
                                                                onClick={() => updateNewCard('card_pattern', p.value)}
                                                                className={`relative overflow-hidden rounded-lg text-xs font-medium transition-all h-9 ${newCard.card_pattern === p.value ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/20' : 'bg-gray-100 text-slate-600 hover:bg-gray-200'}`}
                                                                data-testid={`pattern-${p.value}`}
                                                            >
                                                                {p.name}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                </div>{/* end scrollable */}

                                <DialogFooter className="gap-2 shrink-0 pt-3 border-t border-slate-100">
                                    {wizardStep > 1 && (
                                        <Button variant="outline" onClick={() => setWizardStep(s => s - 1)} data-testid="wizard-back-btn">Retour</Button>
                                    )}
                                    {wizardStep < 4 ? (
                                        <Button
                                            onClick={() => {
                                                if (wizardStep === 2 && (!newCard.name || !newCard.reward_description)) {
                                                    toast.error('Remplissez tous les champs'); return;
                                                }
                                                setWizardStep(s => s + 1);
                                            }}
                                            className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1"
                                            data-testid="wizard-next-btn"
                                        >
                                            Suivant <ArrowRight className="w-4 h-4" />
                                        </Button>
                                    ) : (
                                        <Button onClick={handleSaveCard} className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1" data-testid="wizard-create-btn">
                                            {editingCardId ? 'Enregistrer' : 'Créer la carte'} {editingCardId ? <BadgeCheck className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                                        </Button>
                                    )}
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </TabsContent>

                    {/* SCANNER */}
                    <TabsContent value="scanner" className="space-y-4">
                        <h2 className="font-heading text-xl font-bold">Scanner QR Client</h2>

                        <Card className="border-emerald-100/50 rounded-2xl" data-testid="scanner-section">
                            <CardContent className="p-5 space-y-4">
                                <div className="space-y-2">
                                    <Label className="text-sm font-medium">Code QR du client</Label>
                                    <p className="text-xs text-slate-400">Scannez avec la caméra ou entrez le code manuellement</p>

                                    {/* Camera scan button */}
                                    <Button
                                        onClick={() => setShowCamera(true)}
                                        className="w-full h-14 bg-emerald-500 hover:bg-emerald-600 text-white text-base font-semibold rounded-xl active:scale-[0.98] transition-all"
                                        data-testid="open-camera-btn"
                                    >
                                        <Camera className="w-5 h-5 mr-2" />
                                        Scanner avec la caméra
                                    </Button>

                                    {/* Divider */}
                                    <div className="flex items-center gap-3 py-1">
                                        <div className="flex-1 h-px bg-slate-200" />
                                        <span className="text-xs text-slate-400">ou saisir manuellement</span>
                                        <div className="flex-1 h-px bg-slate-200" />
                                    </div>

                                    {/* Manual input */}
                                    <div className="flex gap-2">
                                        <Input
                                            placeholder="Ex: A1B2C3D4"
                                            value={qrInput}
                                            onChange={e => setQrInput(e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && handleScan()}
                                            className="h-12 font-mono uppercase text-lg tracking-wider"
                                            data-testid="scan-qr-input"
                                        />
                                        <Button
                                            onClick={() => handleScan()}
                                            disabled={scanning}
                                            className="bg-emerald-500 hover:bg-emerald-600 text-white h-12 px-6 active:scale-95 transition-all"
                                            data-testid="scan-btn"
                                        >
                                            {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ScanLine className="w-4 h-4 mr-1.5" /> Scanner</>}
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Scan Result */}
                        {scanResult && (
                            <Card className="border-emerald-200 bg-emerald-50/30 rounded-2xl" data-testid="scan-result">
                                <CardContent className="p-5">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                                            <Users className="w-6 h-6 text-emerald-600" />
                                        </div>
                                        <div>
                                            <h3 className="font-heading font-bold text-lg">{scanResult.customer.name}</h3>
                                            <p className="text-sm text-slate-500">{scanResult.customer.email}</p>
                                        </div>
                                    </div>

                                    {/* Birthday Banner */}
                                    {scanResult.birthday_eligible && scanResult.card.birthday_reward_type && (
                                        <div className="bg-gradient-to-r from-pink-50 to-amber-50 border border-pink-200 rounded-xl p-3 mb-4 flex items-center justify-between" data-testid="birthday-banner">
                                            <div className="flex items-center gap-2">
                                                <span className="text-xl">🎂</span>
                                                <div>
                                                    <p className="text-sm font-bold text-pink-700">Joyeux anniversaire !</p>
                                                    <p className="text-xs text-pink-600">
                                                        {scanResult.card.birthday_reward_type === 'points' && `${scanResult.card.birthday_reward_value} points bonus a offrir`}
                                                        {scanResult.card.birthday_reward_type === 'stamp' && `${scanResult.card.birthday_reward_value} tampon(s) a offrir`}
                                                        {scanResult.card.birthday_reward_type === 'discount' && `-${scanResult.card.birthday_reward_value}% a offrir`}
                                                        {scanResult.card.birthday_reward_type === 'gift' && `${scanResult.card.birthday_reward_description || 'Cadeau a offrir'}`}
                                                    </p>
                                                </div>
                                            </div>
                                            <Button size="sm" onClick={handleBirthdayReward} className="bg-pink-500 hover:bg-pink-600 text-white text-xs" data-testid="birthday-reward-btn">
                                                Offrir
                                            </Button>
                                        </div>
                                    )}

                                    <div className="bg-white rounded-xl p-4 border border-emerald-100 mb-4">
                                        <div className="flex items-center justify-between mb-3">
                                            <span className="text-sm text-slate-600">{scanResult.card.name}</span>
                                            <Badge className={`border-0 text-xs ${
                                                scanResult.card.type === 'stamp' ? 'bg-amber-100 text-amber-700' :
                                                scanResult.card.type === 'points' ? 'bg-blue-100 text-blue-700' :
                                                scanResult.card.type === 'cashback' ? 'bg-green-100 text-green-700' :
                                                scanResult.card.type === 'tier' ? 'bg-purple-100 text-purple-700' :
                                                'bg-cyan-100 text-cyan-700'
                                            }`}>
                                                {scanResult.card.type === 'stamp' ? 'Tampons' : scanResult.card.type === 'points' ? 'Points' : scanResult.card.type === 'cashback' ? 'Cashback' : scanResult.card.type === 'tier' ? 'Paliers' : 'Montant'}
                                            </Badge>
                                        </div>

                                        {scanResult.card.type === 'stamp' ? (
                                            <>
                                                <p className="text-xs text-slate-400 mb-3">Cliquez sur une case vide pour tamponner</p>
                                                <div className="grid grid-cols-5 gap-3 mb-3">
                                                    {Array.from({ length: scanResult.card.stamp_goal }).map((_, i) => {
                                                        const isFilled = i < scanResult.wallet.stamps;
                                                        const isNext = i === scanResult.wallet.stamps;
                                                        const justStamped = i === lastStampedIndex;
                                                        return (
                                                            <button
                                                                key={i}
                                                                onClick={() => !isFilled && handleClickStamp(i)}
                                                                disabled={isFilled || stampLoading}
                                                                className={`
                                                                    w-full aspect-square rounded-xl flex flex-col items-center justify-center gap-1 transition-all duration-300 border-2
                                                                    ${isFilled
                                                                        ? 'bg-emerald-500 border-emerald-500 text-white shadow-md shadow-emerald-500/20'
                                                                        : isNext
                                                                            ? 'bg-emerald-50 border-emerald-300 border-dashed cursor-pointer hover:bg-emerald-100 hover:border-emerald-400 hover:scale-105 active:scale-95'
                                                                            : 'bg-gray-50 border-slate-200 border-dashed cursor-pointer hover:bg-emerald-50 hover:border-emerald-200'
                                                                    }
                                                                    ${justStamped ? 'animate-bounce' : ''}
                                                                `}
                                                                data-testid={`stamp-slot-${i}`}
                                                            >
                                                                {isFilled ? (
                                                                    <Stamp className="w-5 h-5" />
                                                                ) : isNext ? (
                                                                    <>
                                                                        <Plus className="w-4 h-4 text-emerald-500" />
                                                                        <span className="text-[10px] font-medium text-emerald-600">Tampon</span>
                                                                    </>
                                                                ) : (
                                                                    <span className="text-xs font-medium text-slate-300">{i + 1}</span>
                                                                )}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <p className="text-sm font-medium text-slate-700">
                                                        {scanResult.wallet.stamps} / {scanResult.card.stamp_goal} tampons
                                                    </p>
                                                    {scanResult.wallet.stamps >= scanResult.card.stamp_goal && (
                                                        <Badge className="bg-amber-100 text-amber-700 border-0 text-xs animate-pulse">
                                                            <Gift className="w-3 h-3 mr-1" /> Récompense prête !
                                                        </Badge>
                                                    )}
                                                </div>
                                            </>
                                        ) : scanResult.card.type === 'points' ? (
                                            <>
                                                <Progress value={Math.min((scanResult.wallet.points / scanResult.card.reward_points_cost) * 100, 100)} className="h-3 mb-2" />
                                                <p className="text-sm text-slate-600">{scanResult.wallet.points} / {scanResult.card.reward_points_cost} points</p>
                                            </>
                                        ) : scanResult.card.type === 'cashback' ? (
                                            <>
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="text-2xl font-bold text-emerald-600">{(scanResult.wallet.cashback_balance || 0).toFixed(2)} €</span>
                                                    <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">{scanResult.card.cashback_percent}% cashback</span>
                                                </div>
                                                <Progress value={Math.min(((scanResult.wallet.cashback_balance || 0) / scanResult.card.cashback_min_redeem) * 100, 100)} className="h-2 mb-1" />
                                                <p className="text-xs text-slate-400">Min. {scanResult.card.cashback_min_redeem}€ pour utiliser — Total depense: {(scanResult.wallet.total_spent || 0).toFixed(2)}€</p>
                                            </>
                                        ) : scanResult.card.type === 'tier' ? (
                                            <>
                                                {(() => {
                                                    const pts = scanResult.wallet.points || 0;
                                                    const paliers = (scanResult.card.paliers_config || []).sort((a, b) => a.points - b.points);
                                                    return (
                                                        <>
                                                            <div className="flex items-center justify-between mb-3">
                                                                <span className="text-2xl font-bold text-slate-800">{pts} <span className="text-sm font-normal text-slate-400">points</span></span>
                                                            </div>
                                                            {/* Full paliers timeline */}
                                                            <div className="space-y-0">
                                                                {paliers.map((p, i) => {
                                                                    const reached = pts >= p.points;
                                                                    const isCurrent = !reached && (i === 0 || pts >= paliers[i - 1].points);
                                                                    const prevPts = i === 0 ? 0 : paliers[i - 1].points;
                                                                    const progressInStep = isCurrent ? Math.min(((pts - prevPts) / (p.points - prevPts)) * 100, 100) : 0;
                                                                    return (
                                                                        <div key={i} className="flex gap-2.5">
                                                                            <div className="flex flex-col items-center">
                                                                                <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 z-10 ${reached ? 'bg-emerald-500 text-white' : isCurrent ? 'border-2 border-emerald-400 bg-emerald-50' : 'border-2 border-slate-200 bg-white'}`}>
                                                                                    {reached && <Check className="w-3 h-3" />}
                                                                                </div>
                                                                                {i < paliers.length - 1 && <div className={`w-0.5 flex-1 ${reached ? 'bg-emerald-400' : 'bg-slate-200'}`} />}
                                                                            </div>
                                                                            <div className="flex-1 pb-2.5">
                                                                                <div className="flex items-center justify-between">
                                                                                    <span className={`text-xs font-semibold ${reached ? 'text-emerald-700' : isCurrent ? 'text-slate-800' : 'text-slate-400'}`}>{p.reward}</span>
                                                                                    <span className={`text-[10px] font-mono ${reached ? 'text-emerald-500' : 'text-slate-400'}`}>{p.points} pts</span>
                                                                                </div>
                                                                                {isCurrent && (
                                                                                    <div className="mt-0.5">
                                                                                        <div className="bg-slate-200 rounded-full h-1 overflow-hidden">
                                                                                            <div className="bg-emerald-500 rounded-full h-1" style={{ width: `${progressInStep}%` }} />
                                                                                        </div>
                                                                                        <span className="text-[9px] text-slate-500">Encore {p.points - pts} pts</span>
                                                                                    </div>
                                                                                )}
                                                                                {reached && (
                                                                                    <span className="text-[9px] text-emerald-500 font-medium">Atteint</span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </>
                                                    );
                                                })()}
                                            </>
                                        ) : scanResult.card.type === 'amount' ? (
                                            <>
                                                <Progress value={Math.min(((scanResult.wallet.total_spent || 0) / scanResult.card.amount_goal) * 100, 100)} className="h-3 mb-2" />
                                                <p className="text-sm text-slate-600">{(scanResult.wallet.total_spent || 0).toFixed(2)}€ / {scanResult.card.amount_goal}€</p>
                                                {scanResult.wallet.reward_pending && (
                                                    <Badge className="bg-amber-100 text-amber-700 border-0 text-xs animate-pulse mt-1">
                                                        <Gift className="w-3 h-3 mr-1" /> Récompense prête !
                                                    </Badge>
                                                )}
                                            </>
                                        ) : null}
                                    </div>

                                    {/* Action Buttons */}
                                    {(() => {
                                        const cardType = scanResult.card.type;

                                        // Paliers (tier) type: special handling with palier redemption
                                        if (cardType === 'tier') {
                                            const pts = scanResult.wallet.points || 0;
                                            const paliers = (scanResult.card.paliers_config || []).sort((a, b) => a.points - b.points);
                                            const availablePaliers = paliers.filter(p => p.points <= pts);
                                            return (
                                                <div className="space-y-3">
                                                    {/* Purchase input */}
                                                    <div className="flex items-center gap-2">
                                                        <div className="relative">
                                                            <Input type="number" min="0.01" step="0.01" value={addAmount} onChange={e => setAddAmount(parseFloat(e.target.value) || 0)} className="w-28 h-12 text-center text-lg font-bold pr-6" data-testid="add-amount-input" />
                                                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 font-bold">€</span>
                                                        </div>
                                                        <Button onClick={() => handleAddTransaction('add_purchase')} className="bg-emerald-500 hover:bg-emerald-600 text-white flex-1 h-12 text-base font-bold active:scale-95 transition-all shadow-lg shadow-emerald-500/20" data-testid="add-purchase-btn">
                                                            <Plus className="w-5 h-5 mr-1.5" /> Enregistrer achat
                                                        </Button>
                                                    </div>
                                                    {/* All palier rewards - active or locked */}
                                                    <div className="rounded-xl border border-slate-200 overflow-hidden">
                                                        <div className="px-3 py-2 bg-slate-50 border-b border-slate-200">
                                                            <p className="text-xs font-bold text-slate-600 flex items-center gap-1">
                                                                <Gift className="w-3.5 h-3.5" /> Attribuer une remise
                                                            </p>
                                                        </div>
                                                        <div className="divide-y divide-slate-100">
                                                            {paliers.map((p, i) => {
                                                                const canUse = pts >= p.points;
                                                                return (
                                                                    <div key={i} className={`flex items-center justify-between px-3 py-2.5 ${canUse ? 'bg-white' : 'bg-slate-50/50'}`}>
                                                                        <div className="flex items-center gap-2 min-w-0">
                                                                            <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${canUse ? 'bg-emerald-500 text-white' : 'border border-slate-200 bg-white'}`}>
                                                                                {canUse ? <Check className="w-3 h-3" /> : <Lock className="w-2.5 h-2.5 text-slate-300" />}
                                                                            </div>
                                                                            <div className="min-w-0">
                                                                                <span className={`text-sm font-medium truncate block ${canUse ? 'text-slate-800' : 'text-slate-400'}`}>{p.reward}</span>
                                                                                <span className={`text-[10px] ${canUse ? 'text-emerald-600' : 'text-slate-400'}`}>{p.points} pts</span>
                                                                            </div>
                                                                        </div>
                                                                        <Button
                                                                            size="sm"
                                                                            onClick={() => handleAddTransaction('redeem_palier', p.points)}
                                                                            disabled={!canUse || stampLoading}
                                                                            className={`text-xs h-8 px-3 shrink-0 ${canUse ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}
                                                                            data-testid={`redeem-palier-${p.points}`}
                                                                        >
                                                                            {stampLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : canUse ? 'Valider' : `${p.points - pts} pts`}
                                                                        </Button>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        }

                                        const canRedeem =
                                            cardType === 'stamp' ? scanResult.wallet.stamps >= scanResult.card.stamp_goal :
                                            cardType === 'points' ? scanResult.wallet.points >= scanResult.card.reward_points_cost :
                                            cardType === 'cashback' ? (scanResult.wallet.cashback_balance || 0) >= scanResult.card.cashback_min_redeem :
                                            cardType === 'amount' ? scanResult.wallet.reward_pending : false;

                                        if (canRedeem) {
                                            return (
                                                <div className="space-y-3">
                                                    <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 text-center animate-pulse-slow" data-testid="reward-ready-banner">
                                                        <div className="flex items-center justify-center gap-2 mb-2">
                                                            <Gift className="w-6 h-6 text-amber-600" />
                                                            <p className="font-heading font-bold text-lg text-amber-800">
                                                                {cardType === 'cashback' ? `Cashback de ${(scanResult.wallet.cashback_balance || 0).toFixed(2)}€ disponible !` : 'Récompense atteinte !'}
                                                            </p>
                                                        </div>
                                                        <p className="text-sm text-amber-700 mb-1">{scanResult.card.reward_description}</p>
                                                    </div>
                                                    <Button
                                                        onClick={handleRedeemForCustomer}
                                                        disabled={stampLoading}
                                                        className="w-full h-14 bg-amber-500 hover:bg-amber-600 text-white text-lg font-bold active:scale-95 transition-all shadow-lg shadow-amber-500/20 gap-2"
                                                        data-testid="validate-reward-btn"
                                                    >
                                                        {stampLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Award className="w-5 h-5" /> {cardType === 'cashback' ? 'Utiliser le cashback' : 'Valider la récompense'}</>}
                                                    </Button>
                                                </div>
                                            );
                                        }

                                        if (cardType === 'stamp') {
                                            return (
                                                <Button
                                                    onClick={() => handleClickStamp(scanResult.wallet.stamps)}
                                                    disabled={stampLoading}
                                                    className="w-full h-14 bg-emerald-500 hover:bg-emerald-600 text-white text-lg font-bold active:scale-95 transition-all shadow-lg shadow-emerald-500/20 gap-2"
                                                    data-testid="stamp-big-btn"
                                                >
                                                    {stampLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Stamp className="w-5 h-5" /> Tamponner +1</>}
                                                </Button>
                                            );
                                        }

                                        if (cardType === 'points') {
                                            return (
                                                <div className="flex items-center gap-2">
                                                    <Input type="number" min="1" value={addAmount} onChange={e => setAddAmount(parseInt(e.target.value) || 1)} className="w-24 h-12 text-center text-lg font-bold" data-testid="add-amount-input" />
                                                    <Button onClick={() => handleAddTransaction('add_points')} className="bg-emerald-500 hover:bg-emerald-600 text-white flex-1 h-12 text-base font-bold active:scale-95 transition-all shadow-lg shadow-emerald-500/20" data-testid="add-transaction-btn">
                                                        <Plus className="w-5 h-5 mr-1.5" /> Ajouter {addAmount} point{addAmount > 1 ? 's' : ''}
                                                    </Button>
                                                </div>
                                            );
                                        }

                                        // Cashback, Tier, Amount — all need purchase amount input
                                        return (
                                            <div className="flex items-center gap-2">
                                                <div className="relative">
                                                    <Input type="number" min="0.01" step="0.01" value={addAmount} onChange={e => setAddAmount(parseFloat(e.target.value) || 0)} className="w-28 h-12 text-center text-lg font-bold pr-6" data-testid="add-amount-input" />
                                                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 font-bold">€</span>
                                                </div>
                                                <Button onClick={() => handleAddTransaction('add_purchase')} className="bg-emerald-500 hover:bg-emerald-600 text-white flex-1 h-12 text-base font-bold active:scale-95 transition-all shadow-lg shadow-emerald-500/20" data-testid="add-purchase-btn">
                                                    <Plus className="w-5 h-5 mr-1.5" /> Enregistrer achat
                                                </Button>
                                            </div>
                                        );
                                    })()}

                                    {/* Rewards history */}
                                    {scanResult.wallet.rewards_claimed > 0 && (
                                        <p className="text-xs text-center text-slate-400 mt-2">
                                            <Award className="w-3 h-3 inline mr-1" />
                                            {scanResult.wallet.rewards_claimed} récompense(s) déjà offerte(s)
                                        </p>
                                    )}
                                </CardContent>
                            </Card>
                        )}
                    </TabsContent>

                    {/* CUSTOMERS */}
                    <TabsContent value="customers" className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h2 className="font-heading text-xl font-bold">Mes Clients</h2>
                            <div className="flex items-center gap-2">
                                {customers.filter(c => c.rgpd_consent).length > 0 && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50 gap-1"
                                        data-testid="export-csv-btn"
                                        onClick={() => {
                                            const rgpdClients = customers.filter(c => c.rgpd_consent);
                                            const header = 'Nom,Email,Téléphone,Date de naissance,Inscription\n';
                                            const rows = rgpdClients.map(c =>
                                                `"${c.name}","${c.email}","${c.phone || ''}","${c.birthday ? c.birthday.split('-').reverse().join('/') : ''}","${c.created_at ? c.created_at.split('T')[0] : ''}"`
                                            ).join('\n');
                                            const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8;' });
                                            const url = URL.createObjectURL(blob);
                                            const a = document.createElement('a');
                                            a.href = url; a.download = 'clients_fidelrun.csv'; a.click();
                                            URL.revokeObjectURL(url);
                                            toast.success(`${rgpdClients.length} client(s) exporte(s)`);
                                        }}
                                    >
                                        <Download className="w-3.5 h-3.5" /> Exporter CSV
                                    </Button>
                                )}
                                {customers.length > 0 && (
                                    <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs">{customers.length} client{customers.length > 1 ? 's' : ''}</Badge>
                                )}
                            </div>
                        </div>

                        {customers.length === 0 ? (
                            <Card className="border-emerald-100/50 rounded-2xl">
                                <CardContent className="p-8 text-center">
                                    <Users className="w-12 h-12 text-emerald-200 mx-auto mb-4" />
                                    <p className="text-slate-500">Aucun client enregistré</p>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="space-y-3">
                                {customers.map(cust => (
                                    <Card key={cust.id} onClick={() => setSelectedCustomerId(cust.id)} className="border-emerald-100/50 rounded-xl hover:border-emerald-300 hover:shadow-md transition-all cursor-pointer group" data-testid={`customer-${cust.id}`}>
                                        <CardContent className="p-4">
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                                                        <span className="text-sm font-bold text-emerald-700">{cust.name?.charAt(0)?.toUpperCase()}</span>
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium group-hover:text-emerald-700 transition-colors">{cust.name}</p>
                                                        <p className="text-xs text-slate-400">{cust.email}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <div className="text-right">
                                                        {cust.wallets?.map(w => (
                                                            <p key={w.id} className="text-xs text-slate-500">
                                                                {w.stamps > 0 && `${w.stamps} tampons`}
                                                                {w.points > 0 && `${w.points} pts`}
                                                            </p>
                                                        ))}
                                                    </div>
                                                    <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-emerald-500 group-hover:translate-x-0.5 transition-all" />
                                                </div>
                                            </div>

                                            {cust.rgpd_consent ? (
                                                <div className="mt-2 pt-2 border-t border-slate-100 flex flex-wrap items-center gap-x-4 gap-y-1">
                                                    {cust.birthday && (
                                                        <span className="text-xs text-slate-500 flex items-center gap-1">
                                                            <Cake className="w-3 h-3 text-pink-400" />
                                                            {cust.birthday.split('-').reverse().join('/')}
                                                        </span>
                                                    )}
                                                    {cust.phone && (
                                                        <span className="text-xs text-slate-500 flex items-center gap-1">
                                                            <Phone className="w-3 h-3 text-blue-400" />
                                                            {cust.phone}
                                                        </span>
                                                    )}
                                                    <span className="text-[10px] text-emerald-500 flex items-center gap-0.5 ml-auto">
                                                        <ShieldCheck className="w-3 h-3" /> RGPD
                                                    </span>
                                                </div>
                                            ) : (
                                                <div className="mt-2 pt-2 border-t border-slate-100">
                                                    <span className="text-[10px] text-slate-400 flex items-center gap-1">
                                                        <ShieldOff className="w-3 h-3" /> Donnees personnelles non accessibles (consentement RGPD non donne)
                                                    </span>
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    {/* PARTNERS */}
                    <TabsContent value="partners" className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h2 className="font-heading text-xl font-bold">Partenaires</h2>
                            <Badge className="bg-blue-100 text-blue-700 border-0 text-xs">{partnerships.filter(p => p.status === 'accepted').length} actif(s)</Badge>
                        </div>

                        {/* Search & Invite */}
                        <Card className="border-blue-100/50 rounded-xl" data-testid="partner-invite-card">
                            <CardContent className="p-4 space-y-3">
                                <p className="text-sm font-semibold text-slate-700">Inviter un commerçant partenaire</p>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    <input
                                        type="text"
                                        placeholder="Rechercher par nom ou email..."
                                        value={partnerSearch}
                                        onChange={e => searchPartners(e.target.value)}
                                        className="w-full pl-9 pr-4 py-2.5 bg-white border border-blue-100 rounded-xl text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-300 transition-all"
                                        data-testid="partner-search-input"
                                    />
                                </div>
                                {partnerResults.length > 0 && (
                                    <div className="space-y-2">
                                        {partnerResults.map(m => (
                                            <div key={m.id} className="flex items-center justify-between p-3 bg-blue-50/50 rounded-lg border border-blue-100">
                                                <div>
                                                    <p className="text-sm font-medium">{m.business_name || m.name}</p>
                                                    <p className="text-xs text-slate-400">{m.email}</p>
                                                </div>
                                                <Button size="sm" onClick={() => sendPartnerRequest(m.email)} className="bg-blue-500 hover:bg-blue-600 text-white text-xs" data-testid={`invite-partner-${m.id}`}>
                                                    Inviter
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100">
                                    <div>
                                        <Label className="text-xs font-medium">% de points partages</Label>
                                        <Input type="number" min="1" max="100" value={partnerSharePct} onChange={e => setPartnerSharePct(parseInt(e.target.value) || 20)} className="h-9 mt-1" data-testid="partner-share-input" />
                                        <p className="text-[10px] text-slate-400 mt-0.5">Points du partenaire credites au client</p>
                                    </div>
                                    <div>
                                        <Label className="text-xs font-medium">Bonus de bienvenue</Label>
                                        <Input type="number" min="0" value={partnerWelcomeBonus} onChange={e => setPartnerWelcomeBonus(parseInt(e.target.value) || 0)} className="h-9 mt-1" data-testid="partner-welcome-input" />
                                        <p className="text-[10px] text-slate-400 mt-0.5">Offerts quand un client rejoint</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Pending Requests */}
                        {partnerships.filter(p => p.status === 'pending' && p.receiver_id === user?.id).length > 0 && (
                            <div className="space-y-2">
                                <p className="text-sm font-semibold text-orange-600">Demandes recues</p>
                                {partnerships.filter(p => p.status === 'pending' && p.receiver_id === user?.id).map(p => (
                                    <Card key={p.id} className="border-orange-200 rounded-xl" data-testid={`pending-${p.id}`}>
                                        <CardContent className="p-3 flex items-center justify-between">
                                            <div>
                                                <p className="text-sm font-medium">{p.sender_name}</p>
                                                <p className="text-[10px] text-slate-400">{p.share_percent}% de points partages, {p.welcome_bonus} pts de bienvenue</p>
                                            </div>
                                            <div className="flex gap-2">
                                                <Button size="sm" onClick={() => acceptPartner(p.id)} className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs" data-testid={`accept-${p.id}`}>Accepter</Button>
                                                <Button size="sm" variant="outline" onClick={() => rejectPartner(p.id)} className="text-xs border-slate-200" data-testid={`reject-${p.id}`}>Refuser</Button>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )}

                        {/* Sent Pending */}
                        {partnerships.filter(p => p.status === 'pending' && p.sender_id === user?.id).length > 0 && (
                            <div className="space-y-2">
                                <p className="text-sm font-semibold text-slate-500">Demandes envoyees</p>
                                {partnerships.filter(p => p.status === 'pending' && p.sender_id === user?.id).map(p => (
                                    <Card key={p.id} className="border-slate-200 rounded-xl opacity-70" data-testid={`sent-${p.id}`}>
                                        <CardContent className="p-3 flex items-center justify-between">
                                            <div>
                                                <p className="text-sm font-medium">{p.receiver_name}</p>
                                                <Badge className="bg-orange-100 text-orange-600 border-0 text-[10px]">En attente</Badge>
                                            </div>
                                            <Button size="sm" variant="ghost" onClick={() => deletePartner(p.id)} className="text-xs text-slate-400 hover:text-red-500">Annuler</Button>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )}

                        {/* Active Partnerships */}
                        {partnerships.filter(p => p.status === 'accepted').length > 0 && (
                            <div className="space-y-2">
                                <p className="text-sm font-semibold text-emerald-600">Partenaires actifs</p>
                                {partnerships.filter(p => p.status === 'accepted').map(p => {
                                    const partnerName = p.sender_id === user?.id ? p.receiver_name : p.sender_name;
                                    return (
                                        <Card key={p.id} className="border-emerald-200 bg-emerald-50/30 rounded-xl" data-testid={`active-${p.id}`}>
                                            <CardContent className="p-3 flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                                                        <Handshake className="w-5 h-5 text-emerald-600" />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium">{partnerName}</p>
                                                        <p className="text-[10px] text-slate-400">{p.share_percent}% pts partages | {p.welcome_bonus} pts bienvenue</p>
                                                    </div>
                                                </div>
                                                <Button size="sm" variant="ghost" onClick={() => deletePartner(p.id)} className="text-xs text-slate-400 hover:text-red-500" data-testid={`remove-${p.id}`}>Retirer</Button>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </div>
                        )}

                        {partnerships.filter(p => p.status === 'accepted').length === 0 && partnerships.filter(p => p.status === 'pending').length === 0 && (
                            <Card className="border-blue-100/50 rounded-2xl">
                                <CardContent className="p-8 text-center">
                                    <Handshake className="w-12 h-12 text-blue-200 mx-auto mb-4" />
                                    <p className="text-slate-500">Aucun partenaire pour le moment</p>
                                    <p className="text-xs text-slate-400 mt-1">Recherchez un commerçant ci-dessus pour l'inviter</p>
                                </CardContent>
                            </Card>
                        )}
                    </TabsContent>

                    {/* PROMOTIONS TAB */}
                    <TabsContent value="promos" className="space-y-4">
                        {planFeatures && !planFeatures.promos ? (
                            <LockedFeature feature="promos" requiredPlan="pro" />
                        ) : (<>
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="font-heading text-xl font-bold">Promotions</h2>
                                <p className="text-sm text-slate-500">Créez des offres visibles par tous les clients</p>
                            </div>
                            <Button onClick={() => openPromoDialog()} className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1.5" data-testid="create-promo-btn">
                                <Plus className="w-4 h-4" /> Nouvelle promo
                            </Button>
                        </div>

                        {promos.length === 0 ? (
                            <Card className="border-emerald-100/50 rounded-2xl">
                                <CardContent className="p-8 text-center">
                                    <Tag className="w-12 h-12 text-emerald-200 mx-auto mb-4" />
                                    <p className="text-slate-500">Aucune promotion pour le moment</p>
                                    <p className="text-xs text-slate-400 mt-1">Créez votre première offre pour attirer de nouveaux clients</p>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="space-y-3">
                                {promos.map(p => {
                                    const status = getPromoStatus(p);
                                    const daysLeft = Math.ceil((new Date(p.end_date) - new Date()) / 86400000);
                                    return (
                                        <Card key={p.id} className="border-slate-200 rounded-xl overflow-hidden" data-testid={`promo-${p.id}`}>
                                            <CardContent className="p-0">
                                                <div className="flex">
                                                    {p.photo && (
                                                        <div className="w-32 h-32 shrink-0">
                                                            <img src={p.photo} alt={p.title} className="w-full h-full object-cover" />
                                                        </div>
                                                    )}
                                                    <div className="flex-1 p-4">
                                                        <div className="flex items-start justify-between">
                                                            <div>
                                                                <div className="flex items-center gap-2 mb-1">
                                                                    <p className="font-bold text-sm">{p.title}</p>
                                                                    <Badge className={`${status.color} border-0 text-[10px]`}>{status.label}</Badge>
                                                                </div>
                                                                {p.description && <p className="text-xs text-slate-500 mb-2 line-clamp-2">{p.description}</p>}
                                                                <div className="flex items-center gap-3 text-xs">
                                                                    {p.original_price > 0 && <span className="line-through text-slate-400">{p.original_price}€</span>}
                                                                    {p.promo_price > 0 && <span className="font-bold text-emerald-600 text-sm">{p.promo_price}€</span>}
                                                                    {p.promo_code && <Badge className="bg-purple-100 text-purple-700 border-0 text-[10px]">Code: {p.promo_code}</Badge>}
                                                                    {p.limited_quantity > 0 && <span className="text-slate-400">Limite: {p.limited_quantity}</span>}
                                                                </div>
                                                                <div className="flex items-center gap-2 mt-2 text-[10px] text-slate-400">
                                                                    <CalendarDays className="w-3 h-3" />
                                                                    {p.start_date} → {p.end_date}
                                                                    {daysLeft > 0 && daysLeft <= 7 && status.label === 'Active' && (
                                                                        <Badge className="bg-orange-100 text-orange-600 border-0 text-[9px]">Plus que {daysLeft}j</Badge>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="flex gap-1 shrink-0">
                                                                <Button size="sm" variant="ghost" onClick={() => openPromoDialog(p)} className="text-slate-400 hover:text-emerald-600 h-8 w-8 p-0" data-testid={`edit-promo-${p.id}`}>
                                                                    <Pencil className="w-3.5 h-3.5" />
                                                                </Button>
                                                                <Button size="sm" variant="ghost" onClick={() => deletePromo(p.id)} className="text-slate-400 hover:text-red-500 h-8 w-8 p-0" data-testid={`delete-promo-${p.id}`}>
                                                                    <Trash2 className="w-3.5 h-3.5" />
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </div>
                        )}
                        </>)}
                    </TabsContent>

                    {/* NOTIFICATIONS TAB */}
                    <TabsContent value="notifications" className="space-y-4">
                        {planFeatures && !planFeatures.push ? (
                            <LockedFeature feature="notifications" requiredPlan="pro" />
                        ) : (<>
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="font-heading text-xl font-bold">Notifications Push</h2>
                                <p className="text-sm text-slate-500">Envoyez des notifications a vos clients</p>
                            </div>
                            <Button onClick={() => setPushDialogOpen(true)} className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1.5" data-testid="send-push-btn">
                                <Send className="w-4 h-4" /> Envoyer
                            </Button>
                        </div>

                        {pushHistory.length === 0 ? (
                            <Card className="border-emerald-100/50 rounded-2xl">
                                <CardContent className="p-8 text-center">
                                    <Bell className="w-12 h-12 text-emerald-200 mx-auto mb-4" />
                                    <p className="text-slate-500">Aucune notification envoyee</p>
                                    <p className="text-xs text-slate-400 mt-1">Envoyez votre première notification push a vos clients</p>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="space-y-3">
                                {pushHistory.map(p => (
                                    <Card key={p.id} className="border-slate-200 rounded-xl" data-testid={`push-${p.id}`}>
                                        <CardContent className="p-4">
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <p className="font-bold text-sm">{p.title}</p>
                                                    <p className="text-xs text-slate-500 mt-0.5">{p.message}</p>
                                                </div>
                                                <div className="text-right shrink-0 ml-3">
                                                    <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">{p.recipients || 0} push</Badge>
                                                </div>
                                            </div>
                                            <p className="text-[10px] text-slate-400 mt-2">
                                                {new Date(p.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                            </p>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )}
                        </>)}
                    </TabsContent>

                    {/* ACTIVITY LOGS */}
                    <TabsContent value="activity" className="space-y-4" data-testid="activity-content">
                        {planFeatures && !planFeatures.activity_logs ? (
                            <LockedFeature feature="activity" requiredPlan="pro" />
                        ) : (<>
                        {activityLoading && !activityLogs.length ? (
                            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent" /></div>
                        ) : (
                            <>
                                {/* Summary cards */}
                                {activitySummary && (
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                        <Card className="border-slate-200 rounded-xl">
                                            <CardContent className="p-4 text-center">
                                                <ScanLine className="w-5 h-5 text-emerald-500 mx-auto mb-1" />
                                                <p className="text-2xl font-bold text-slate-800" data-testid="scans-today">{activitySummary.scans_today}</p>
                                                <p className="text-[11px] text-slate-500">Scans aujourd'hui</p>
                                            </CardContent>
                                        </Card>
                                        <Card className="border-slate-200 rounded-xl">
                                            <CardContent className="p-4 text-center">
                                                <ScanLine className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                                                <p className="text-2xl font-bold text-slate-800" data-testid="scans-week">{activitySummary.scans_week}</p>
                                                <p className="text-[11px] text-slate-500">Scans cette semaine</p>
                                            </CardContent>
                                        </Card>
                                        <Card className="border-slate-200 rounded-xl">
                                            <CardContent className="p-4 text-center">
                                                <TrendingUp className="w-5 h-5 text-orange-500 mx-auto mb-1" />
                                                <p className="text-2xl font-bold text-slate-800" data-testid="tx-today">{activitySummary.transactions_today}</p>
                                                <p className="text-[11px] text-slate-500">Actions aujourd'hui</p>
                                            </CardContent>
                                        </Card>
                                        <Card className="border-slate-200 rounded-xl">
                                            <CardContent className="p-4 text-center">
                                                <TrendingUp className="w-5 h-5 text-purple-500 mx-auto mb-1" />
                                                <p className="text-2xl font-bold text-slate-800" data-testid="tx-week">{activitySummary.transactions_week}</p>
                                                <p className="text-[11px] text-slate-500">Actions cette semaine</p>
                                            </CardContent>
                                        </Card>
                                    </div>
                                )}

                                {/* Filter */}
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Filter className="w-4 h-4 text-slate-400" />
                                    <Select value={activityFilter || 'all'} onValueChange={(v) => { const f = v === 'all' ? '' : v; setActivityFilter(f); fetchActivityLogs(f); }}>
                                        <SelectTrigger className="w-[200px] h-9 text-sm rounded-lg" data-testid="activity-filter">
                                            <SelectValue placeholder="Toutes les actions" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">Toutes les actions</SelectItem>
                                            <SelectItem value="scan">Scans QR</SelectItem>
                                            <SelectItem value="tampon_ajoute">Tampons ajoutés</SelectItem>
                                            <SelectItem value="points_ajoutes">Points ajoutés</SelectItem>
                                            <SelectItem value="achat_enregistre">Achats enregistrés</SelectItem>
                                            <SelectItem value="recompense_reclamee">Récompenses réclamées</SelectItem>
                                            <SelectItem value="cashback_utilise">Cashback utilisé</SelectItem>
                                            <SelectItem value="cadeau_anniversaire">Cadeaux anniversaire</SelectItem>
                                            <SelectItem value="promo_creee">Promotions créées</SelectItem>
                                            <SelectItem value="notification_envoyee">Notifications envoyées</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    {activityFilter && activityFilter !== 'all' && (
                                        <Button variant="ghost" size="sm" className="text-xs text-slate-500" onClick={() => { setActivityFilter(''); fetchActivityLogs(''); }} data-testid="clear-filter">
                                            <X className="w-3 h-3 mr-1" /> Effacer
                                        </Button>
                                    )}
                                </div>

                                {/* Logs list */}
                                {activityLogs.length === 0 ? (
                                    <div className="text-center py-12">
                                        <Activity className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                                        <p className="text-slate-500 text-sm">Aucune activité enregistrée</p>
                                        <p className="text-slate-400 text-xs mt-1">Les scans et transactions apparaîtront ici</p>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {activityLogs.map(log => {
                                            const actionConfig = {
                                                scan: { label: 'Scan QR', icon: ScanLine, color: 'text-emerald-600 bg-emerald-50' },
                                                tampon_ajoute: { label: 'Tampon ajouté', icon: Stamp, color: 'text-blue-600 bg-blue-50' },
                                                points_ajoutes: { label: 'Points ajoutés', icon: Star, color: 'text-amber-600 bg-amber-50' },
                                                achat_enregistre: { label: 'Achat enregistré', icon: Euro, color: 'text-green-600 bg-green-50' },
                                                recompense_reclamee: { label: 'Récompense réclamée', icon: Gift, color: 'text-purple-600 bg-purple-50' },
                                                cashback_utilise: { label: 'Cashback utilisé', icon: DollarSign, color: 'text-teal-600 bg-teal-50' },
                                                cadeau_anniversaire: { label: 'Cadeau anniversaire', icon: Cake, color: 'text-pink-600 bg-pink-50' },
                                                promo_creee: { label: 'Promo créée', icon: Tag, color: 'text-orange-600 bg-orange-50' },
                                                notification_envoyee: { label: 'Notification envoyée', icon: Bell, color: 'text-indigo-600 bg-indigo-50' },
                                                points_retires: { label: 'Points retirés', icon: Star, color: 'text-red-600 bg-red-50' },
                                            };
                                            const config = actionConfig[log.action] || { label: log.action, icon: Activity, color: 'text-slate-600 bg-slate-50' };
                                            const ActionIcon = config.icon;
                                            return (
                                                <Card key={log.id} className="border-slate-100 rounded-xl" data-testid={`activity-log-${log.id}`}>
                                                    <CardContent className="p-3 flex items-center gap-3">
                                                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${config.color}`}>
                                                            <ActionIcon className="w-4 h-4" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-sm font-semibold text-slate-800">{config.label}</span>
                                                                {log.details?.amount > 0 && (
                                                                    <Badge className="bg-slate-100 text-slate-600 border-0 text-[10px]">
                                                                        {log.action === 'achat_enregistre' ? `${log.details.amount}€` : `+${log.details.amount}`}
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                            {log.customer_name && (
                                                                <p className="text-xs text-slate-500 truncate">
                                                                    <Users className="w-3 h-3 inline mr-1" />{log.customer_name}
                                                                    {log.card_name ? ` - ${log.card_name}` : ''}
                                                                </p>
                                                            )}
                                                            {!log.customer_name && log.details?.title && (
                                                                <p className="text-xs text-slate-500 truncate">{log.details.title}</p>
                                                            )}
                                                        </div>
                                                        <p className="text-[10px] text-slate-400 shrink-0">
                                                            {new Date(log.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                                        </p>
                                                    </CardContent>
                                                </Card>
                                            );
                                        })}
                                    </div>
                                )}
                            </>
                        )}
                        </>)}
                    </TabsContent>
                </Tabs>

                {/* Push Notification Dialog */}
                <Dialog open={pushDialogOpen} onOpenChange={setPushDialogOpen}>
                    <DialogContent className="rounded-2xl sm:max-w-sm">
                        <DialogHeader>
                            <DialogTitle className="font-heading">Envoyer une notification</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                            <div>
                                <Label>Titre *</Label>
                                <Input value={pushForm.title} onChange={e => setPushForm(p => ({ ...p, title: e.target.value }))} placeholder="Ex: Offre speciale !" data-testid="push-title-input" />
                            </div>
                            <div>
                                <Label>Message *</Label>
                                <textarea value={pushForm.message} onChange={e => setPushForm(p => ({ ...p, message: e.target.value }))} placeholder="Ex: -20% sur tout le magasin aujourd'hui !" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm min-h-[100px] focus:outline-none focus:ring-2 focus:ring-emerald-200" data-testid="push-message-input" />
                            </div>
                            <div className="p-3 bg-blue-50 rounded-lg">
                                <p className="text-xs text-blue-600">La notification sera envoyee en push (telephone) et dans l'app (cloche) a tous vos clients fideles.</p>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="ghost" onClick={() => setPushDialogOpen(false)}>Annuler</Button>
                            <Button onClick={sendPushNotification} disabled={pushSending} className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1.5" data-testid="confirm-push-btn">
                                {pushSending ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : <Send className="w-4 h-4" />}
                                {pushSending ? 'Envoi...' : 'Envoyer'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Promo Create/Edit Dialog */}
                <Dialog open={promoDialogOpen} onOpenChange={setPromoDialogOpen}>
                    <DialogContent className="rounded-2xl sm:max-w-md max-h-[85vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="font-heading">{editingPromo ? 'Modifier la promo' : 'Nouvelle promotion'}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                            <div>
                                <Label>Titre *</Label>
                                <Input value={promoForm.title} onChange={e => setPromoForm(p => ({ ...p, title: e.target.value }))} placeholder="Ex: 2 pizzas = 1 offerte" data-testid="promo-title-input" />
                            </div>
                            <div>
                                <Label>Description</Label>
                                <textarea value={promoForm.description} onChange={e => setPromoForm(p => ({ ...p, description: e.target.value }))} placeholder="Details de l'offre..." className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-emerald-200" data-testid="promo-desc-input" />
                            </div>
                            <div>
                                <Label>Photo</Label>
                                <div className="flex items-center gap-3">
                                    {promoForm.photo ? (
                                        <div className="relative w-20 h-20 rounded-lg overflow-hidden border">
                                            <img src={promoForm.photo} alt="promo" className="w-full h-full object-cover" />
                                            <button onClick={() => setPromoForm(p => ({ ...p, photo: '' }))} className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs">
                                                <X className="w-3 h-3" />
                                            </button>
                                        </div>
                                    ) : (
                                        <label className="w-20 h-20 border-2 border-dashed border-slate-200 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-emerald-300 transition-colors">
                                            <ImageIcon className="w-5 h-5 text-slate-300" />
                                            <span className="text-[9px] text-slate-400 mt-1">Ajouter</span>
                                            <input type="file" accept="image/*" onChange={handlePromoPhotoUpload} className="hidden" data-testid="promo-photo-input" />
                                        </label>
                                    )}
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Prix original (€)</Label>
                                    <Input type="number" step="0.01" value={promoForm.original_price} onChange={e => setPromoForm(p => ({ ...p, original_price: e.target.value }))} placeholder="30.00" data-testid="promo-orig-price" />
                                </div>
                                <div>
                                    <Label>Prix promo (€)</Label>
                                    <Input type="number" step="0.01" value={promoForm.promo_price} onChange={e => setPromoForm(p => ({ ...p, promo_price: e.target.value }))} placeholder="20.00" data-testid="promo-promo-price" />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Date debut *</Label>
                                    <Input type="date" value={promoForm.start_date} onChange={e => setPromoForm(p => ({ ...p, start_date: e.target.value }))} data-testid="promo-start-date" />
                                </div>
                                <div>
                                    <Label>Date fin *</Label>
                                    <Input type="date" value={promoForm.end_date} onChange={e => setPromoForm(p => ({ ...p, end_date: e.target.value }))} data-testid="promo-end-date" />
                                </div>
                            </div>
                            <div>
                                <Label>Conditions</Label>
                                <Input value={promoForm.conditions} onChange={e => setPromoForm(p => ({ ...p, conditions: e.target.value }))} placeholder="Ex: Valable du lundi au vendredi" data-testid="promo-conditions" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Quantite limitee</Label>
                                    <Input type="number" value={promoForm.limited_quantity} onChange={e => setPromoForm(p => ({ ...p, limited_quantity: e.target.value }))} placeholder="0 = illimite" data-testid="promo-qty" />
                                </div>
                                <div>
                                    <Label>Code promo</Label>
                                    <Input value={promoForm.promo_code} onChange={e => setPromoForm(p => ({ ...p, promo_code: e.target.value }))} placeholder="Ex: PIZZA50" data-testid="promo-code" />
                                </div>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="ghost" onClick={() => setPromoDialogOpen(false)}>Annuler</Button>
                            <Button onClick={savePromo} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="save-promo-btn">
                                {editingPromo ? 'Mettre a jour' : 'Publier la promo'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </main>

            {/* QR Camera Scanner Overlay */}
            {showCamera && (
                <QrCameraScanner
                    onScan={handleCameraScan}
                    onClose={() => setShowCamera(false)}
                />
            )}

            {/* Share Card Dialog */}
            <Dialog open={!!shareCard} onOpenChange={() => setShareCard(null)}>
                <DialogContent className="rounded-2xl p-0 sm:max-w-[400px]">
                    {shareCard && (() => {
                        const shareUrl = `${window.location.origin}/rejoindre/${shareCard.id}`;
                        const handleCopy = () => {
                            navigator.clipboard.writeText(shareUrl);
                            setLinkCopied(true);
                            toast.success('Lien copié !');
                            setTimeout(() => setLinkCopied(false), 2000);
                        };
                        const handleDownloadQr = () => {
                            const svg = document.getElementById('share-qr-svg');
                            if (!svg) return;
                            const svgData = new XMLSerializer().serializeToString(svg);
                            const canvas = document.createElement('canvas');
                            canvas.width = 512; canvas.height = 512;
                            const ctx = canvas.getContext('2d');
                            const img = new Image();
                            img.onload = () => {
                                ctx.fillStyle = '#ffffff';
                                ctx.fillRect(0, 0, 512, 512);
                                ctx.drawImage(img, 0, 0, 512, 512);
                                const a = document.createElement('a');
                                a.download = `fidelrun-${shareCard.name.replace(/\s+/g, '-')}.png`;
                                a.href = canvas.toDataURL('image/png');
                                a.click();
                            };
                            img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
                        };
                        return (
                            <div className="p-6 pt-8 space-y-4 overflow-hidden">
                                <h3 className="text-lg font-semibold text-center">Partager le programme</h3>
                                <p className="text-center text-sm text-slate-500">{shareCard.name}</p>
                                <div className="flex justify-center bg-slate-50 rounded-xl p-4">
                                    <QRCodeSVG id="share-qr-svg" value={shareUrl} size={160} level="H" data-testid="share-qr-code" />
                                </div>
                                <p className="text-center text-xs text-slate-400">Scannez ou partagez le lien ci-dessous</p>
                                <div className="flex items-center gap-2 overflow-hidden">
                                    <div className="flex-1 min-w-0 bg-slate-50 rounded-lg px-3 py-2 text-xs text-slate-500 font-mono truncate border" data-testid="share-link-text">
                                        {shareUrl}
                                    </div>
                                    <Button size="icon" variant={linkCopied ? "default" : "outline"} onClick={handleCopy} className={`shrink-0 h-9 w-9 ${linkCopied ? 'bg-emerald-500 text-white' : ''}`} data-testid="copy-share-link">
                                        {linkCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                    </Button>
                                </div>
                                <Button onClick={handleDownloadQr} variant="outline" className="w-full gap-2 text-sm" data-testid="download-qr-btn">
                                    <Download className="w-4 h-4" /> Télécharger le QR code
                                </Button>
                            </div>
                        );
                    })()}
                </DialogContent>
            </Dialog>
            <Footer role="merchant" />

            <CustomerDetailDialog
                open={!!selectedCustomerId}
                customerId={selectedCustomerId}
                onClose={() => setSelectedCustomerId(null)}
                onChanged={() => { fetchData(); }}
            />
        </div>
    );
}
